import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, Link, useHistory, useLocation } from 'react-router-dom';
import Layout from './Layout';
import EventContext from '../contexts/EventContext';
import useApiUrlTranslation from './useApiUrlTranslation';
import DragDropVideoUpload from './DragDropVideoUpload';
import { API_URL, DIR_IMG_UPLOADED_CONTACTS } from '../config';
import { logActivity, useActivityLog } from '../customHooks/useActivityLog';
import {
  ArrowLeft, Sparkles, UploadCloud, CheckCircle, XCircle, Copy, Check,
  User, Building, Mail, Search, Film, Plus, X, Loader as SpinnerIcon,
  ExternalLink, Youtube, Download, Share2, Image, Zap, CalendarCheck,
  AlertTriangle, Trash2, Pencil
} from 'lucide-react';
import { PhoneInputField } from './PhoneNumberInput';

const UPLOAD_API_URL = 'https://manager.event2one.com/videos/upload-to-youtube/upload_api.php';
const UPLOAD_API_KEY = 'mgv_yt_upload_2026';

const TABS = [
  { id: 'metadata', label: 'Métadonnées' },
  { id: 'upload', label: 'Upload' },
  { id: 'links', label: 'Liens' },
  { id: 'posts', label: 'Social' },
  { id: 'cycles', label: 'Collectifs' },
  { id: 'speaker', label: 'Intervenants' },
  { id: 'delivery', label: 'Livraison' },
  { id: 'params', label: 'Paramètres' },
];

// ─── helpers ────────────────────────────────────────────────────────────────

const extractYoutubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v\/|embed\/|watch\?v=|&v=)([^#&?]{11})/);
  return m ? m[1] : null;
};

const LANG_INFO = {
  fr: { flag: '🇫🇷', label: 'Français' },
  en: { flag: '🇬🇧', label: 'English' },
};

const LangueBadge = ({ langue }) => {
  const info = LANG_INFO[langue] || LANG_INFO.fr;
  return (
    <span
      title="Langue d'interview"
      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:text-neutral-200 bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-full shrink-0"
    >
      <span className="text-sm leading-none">{info.flag}</span>
      Langue d'interview : {info.label}
    </span>
  );
};

const YtPlayButton = ({ scale = 1 }) => (
  <svg viewBox="0 0 68 48" style={{ width: 68 * scale, height: 48 * scale }} className="drop-shadow-lg" xmlns="http://www.w3.org/2000/svg">
    <path d="M66.5 7.7c-.8-2.9-2.9-5.1-5.8-5.9C55.8.5 34 .5 34 .5s-21.8 0-26.7 1.3c-2.9.8-5 3-5.8 5.9C.2 12.7.2 24 .2 24s0 11.3 1.3 16.3c.8 2.9 2.9 5.1 5.8 5.9C12.2 47.5 34 47.5 34 47.5s21.8 0 26.7-1.3c2.9-.8 5-3 5.8-5.9C67.8 35.3 67.8 24 67.8 24s0-11.3-1.3-16.3z" fill="#FF0000" fillOpacity="0.9" />
    <path d="M27 33.5l18-9.5-18-9.5v19z" fill="white" />
  </svg>
);

// ─── VideoPlayer ─────────────────────────────────────────────────────────────

const VideoPlayer = ({ video }) => {
  const [active, setActive] = useState(false);
  const youtubeId = video?.id_youtube || (video?.video_url ? extractYoutubeId(video.video_url) : null);
  const thumbnail = video?.thumbnail_large_url || video?.thumbnail_medium_url
    || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null);

  if (youtubeId) {
    if (!active) {
      return (
        <button type="button" className="w-full h-full relative group focus:outline-none bg-black" onClick={() => setActive(true)}>
          {thumbnail
            ? <img src={thumbnail} alt="Aperçu vidéo" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            : <div className="w-full h-full bg-gray-900" />}
          <span className="absolute inset-0 flex items-center justify-center transition-transform group-hover:scale-110">
            <YtPlayButton />
          </span>
        </button>
      );
    }
    return (
      <iframe
        src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
        title="Vidéo"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    );
  }

  if (video?.embed_url) {
    if (!active) {
      return (
        <button type="button" className="w-full h-full relative group focus:outline-none bg-black" onClick={() => setActive(true)}>
          {thumbnail
            ? <img src={thumbnail} alt="Aperçu vidéo" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            : <div className="w-full h-full bg-gray-900" />}
          <span className="absolute inset-0 flex items-center justify-center transition-transform group-hover:scale-110">
            <YtPlayButton />
          </span>
        </button>
      );
    }
    return (
      <iframe src={video.embed_url} title="Vidéo" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen className="w-full h-full" />
    );
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-gray-500">
      <Film size={48} />
      <span className="mt-3 text-sm">Aucune vidéo disponible</span>
    </div>
  );
};

// ─── Tab: Métadonnées ─────────────────────────────────────────────────────────

const TabMetadata = ({ video, contact, presta, onVideoUpdated, organisationId = 1, confEventId }) => {
  const { apiUrlTranslation } = useApiUrlTranslation();
  const [titre, setTitre] = useState(video?.titre || '');
  const [description, setDescription] = useState(video?.description || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [transcribeStatus, setTranscribeStatus] = useState('idle');
  const [transcribeProgress, setTranscribeProgress] = useState(0);
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleGenerateChapters = async () => {
    if (!video?.id_video) return;
    setChaptersLoading(true);
    try {
      const fd = new FormData();
      fd.append('action', 'generate_chapters');
      fd.append('id_video', video.id_video);
      fd.append('id_youtube', video?.id_youtube || '');
      fd.append('update_youtube', '1');
      fd.append('lang', presta?.langue_enregistrement || 'fr');
      const res = await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd });
      const d = await res.json();
      if (d.chapters?.length) setChapters(d.chapters);
    } catch (e) { /* silent */ }
    finally { setChaptersLoading(false); }
  };

  useEffect(() => {
    setTitre(video?.titre || '');
    setDescription(video?.description || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.id_video]);

  const handleSave = async () => {
    if (!video?.id_video) return;
    setSaving(true);
    try {
      // 1. Mise à jour BDD
      await fetch(`${API_URL}?action=updateVideo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_video: video.id_video, titre, description }),
      });

      // 2. Sync YouTube si la vidéo a un id_youtube
      if (video.id_youtube) {
        fetch(UPLOAD_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Api-Key': UPLOAD_API_KEY },
          body: JSON.stringify({
            action: 'update_youtube_metadata',
            id_video: video.id_video,
            titre,
            description,
            organisation_id: organisationId,
          }),
        }).catch(() => { }); // fire-and-forget — la BDD est déjà à jour
      }

      setSaved(true);
      onVideoUpdated?.({ ...video, titre, description });
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleAiSuggest = async () => {
    if (!video?.id_video && !video?.id_youtube) {
      alert('Uploadez d\'abord une vidéo ou ajoutez un lien YouTube pour générer des suggestions IA.');
      return;
    }
    setAiLoading(true);
    try {
      // Fetch participants à la volée — garantit données fraîches même après ajout/suppression
      let contactList = [];
      if (confEventId && apiUrlTranslation) {
        const [animData, intervenData] = await Promise.all([
          fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId} AND cf.statut=27`).then(r => r.json()).catch(() => []),
          fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId} AND cf.statut!=27`).then(r => r.json()).catch(() => []),
        ]);
        const animateurs = Array.isArray(animData) ? animData.filter(d => d.contact) : [];
        const intervenants = Array.isArray(intervenData) ? intervenData.filter(d => d.contact) : [];
        contactList = [
          ...intervenants.map(p => ({ id_contact: p.contact.id_contact, prenom: p.contact.prenom || '', nom: p.contact.nom || '', societe: p.contact.societe || '', role: 'intervenant' })),
          ...animateurs.map(p => ({ id_contact: p.contact.id_contact, prenom: p.contact.prenom || '', nom: p.contact.nom || '', societe: p.contact.societe || '', role: 'animateur' })),
        ];
      }

      const fd = new FormData();
      fd.append('action', 'suggest_from_video');
      if (video?.id_video) fd.append('id_video', video.id_video);
      if (video?.id_youtube) fd.append('id_youtube', video.id_youtube);

      if (contactList.length > 0) {
        fd.append('contacts', JSON.stringify(contactList));
      } else if (contact?.id_contact) {
        fd.append('id_contact', contact.id_contact);
      }

      if (presta?.id_presta) fd.append('id_presta', presta.id_presta);
      fd.append('lang', presta?.langue_enregistrement || 'fr');

      const res = await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Réponse inattendue : ${text.slice(0, 200)}`); }
      if (data.ai) {
        setAiSuggestions(data.ai);
        if (data.ai.titre) setTitre(data.ai.titre);
        if (data.ai.description) setDescription(data.ai.description);
      } else {
        alert(data.error || 'Erreur lors de la génération des suggestions IA.');
      }
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleTranscribeFile = (file) => {
    if (!file?.type.startsWith('video/')) { alert('Sélectionnez un fichier vidéo.'); return; }
    setTranscribeStatus('uploading');
    setTranscribeProgress(0);
    const fd = new FormData();
    fd.append('action', 'transcribe_only');
    fd.append('video', file);
    if (video?.id_video) fd.append('id_video', video.id_video);
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = e => { if (e.lengthComputable) setTranscribeProgress(Math.round(e.loaded / e.total * 100)); };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        setTranscribeStatus(data.ia_transcription ? 'done' : 'error');
        if (!data.ia_transcription) alert(data.error || 'Transcription échouée.');
      } catch { setTranscribeStatus('error'); }
    };
    xhr.onerror = () => setTranscribeStatus('error');
    xhr.open('POST', UPLOAD_API_URL);
    xhr.setRequestHeader('X-Api-Key', UPLOAD_API_KEY);
    xhr.send(fd);
  };

  return (
    <div className="space-y-5">
      {/* AI toolbar */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleAiSuggest}
          disabled={aiLoading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 transition-colors"
        >
          {aiLoading ? <SpinnerIcon size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {aiLoading ? 'Analyse en cours…' : 'Suggestion IA'}
        </button>
        <span className="text-xs text-gray-400 dark:text-neutral-500">Génère titre, description et post LinkedIn depuis la vidéo</span>
      </div>

      {/* Transcription from native file */}
      <div className="rounded-lg border border-dashed border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 p-4">
        <p className="text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-2">Transcription depuis le fichier natif</p>
        {transcribeStatus === 'idle' && (
          <>
            <input ref={fileInputRef} type="file" accept="video/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleTranscribeFile(f); }} />
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-gray-200 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600 rounded-md">
              <UploadCloud size={13} /> Ajouter le fichier natif
            </button>
          </>
        )}
        {transcribeStatus === 'uploading' && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-e2o-500">
              <SpinnerIcon size={12} className="animate-spin" />
              <span>Envoi & transcription… {transcribeProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-neutral-700 rounded-full h-1.5">
              <div className="bg-e2o-500 h-1.5 rounded-full transition-all" style={{ width: `${transcribeProgress}%` }} />
            </div>
          </div>
        )}
        {transcribeStatus === 'done' && (
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs text-green-700"><CheckCircle size={12} />Transcription enregistrée — cliquez sur "Suggestion IA"</span>
            <button type="button" onClick={() => setTranscribeStatus('idle')} className="text-xs text-gray-400 dark:text-neutral-500 hover:underline">Recommencer</button>
          </div>
        )}
        {transcribeStatus === 'error' && (
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs text-red-600"><XCircle size={12} />Échec</span>
            <button type="button" onClick={() => setTranscribeStatus('idle')} className="text-xs text-gray-400 dark:text-neutral-500 hover:underline">Réessayer</button>
          </div>
        )}
      </div>

      {/* Titre */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-200 mb-1.5">Titre</label>
        <input
          type="text"
          value={titre}
          onChange={e => setTitre(e.target.value)}
          className="w-full px-3 py-2.5 border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-e2o-400 placeholder:text-gray-400 dark:placeholder:text-neutral-500"
          placeholder="Titre de la vidéo"
        />
        {aiSuggestions?.titre && aiSuggestions.titre !== titre && (
          <div className="flex items-start gap-2 mt-1.5 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2.5">
            <Sparkles size={13} className="text-yellow-500 mt-0.5 shrink-0" />
            <span className="text-xs text-gray-700 dark:text-neutral-300 flex-1">{aiSuggestions.titre}</span>
            <button type="button" onClick={() => setTitre(aiSuggestions.titre)} className="text-xs text-e2o-500 hover:underline shrink-0">Appliquer</button>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-neutral-200 mb-1.5">Description</label>
        <textarea
          value={description}
          onChange={e => {
            setDescription(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
          rows={4}
          className="w-full px-3 py-2.5 border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-e2o-400 resize-none overflow-hidden placeholder:text-gray-400 dark:placeholder:text-neutral-500"
          placeholder="Description de la vidéo"
        />
        {aiSuggestions?.description && aiSuggestions.description !== description && (
          <div className="flex items-start gap-2 mt-1.5 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2.5">
            <Sparkles size={13} className="text-yellow-500 mt-0.5 shrink-0" />
            <span className="text-xs text-gray-700 dark:text-neutral-300 flex-1 line-clamp-3">{aiSuggestions.description}</span>
            <button type="button" onClick={() => setDescription(aiSuggestions.description)} className="text-xs text-e2o-500 hover:underline shrink-0">Appliquer</button>
          </div>
        )}
      </div>

      {/* LinkedIn post */}
      {aiSuggestions?.post_linkedin && (
        <div className="rounded-lg border border-[#0A66C2]/20 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg viewBox="0 0 24 24" fill="#0A66C2" className="w-4 h-4">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            <span className="text-xs font-semibold text-[#0A66C2]">Post LinkedIn suggéré</span>
          </div>
          <p className="text-xs text-gray-700 dark:text-neutral-300 whitespace-pre-line line-clamp-4">{aiSuggestions.post_linkedin}</p>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(aiSuggestions.post_linkedin)}
            className="mt-2 flex items-center gap-1.5 text-xs text-[#0A66C2] hover:underline"
          >
            <Copy size={11} /> Copier le post
          </button>
        </div>
      )}

      {/* Chapitres YouTube */}
      {video?.id_youtube && (
        <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Chapitres YouTube</p>
            <button
              type="button"
              onClick={handleGenerateChapters}
              disabled={chaptersLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 disabled:opacity-50 transition-colors"
            >
              <Sparkles size={11} />
              {chaptersLoading ? 'Génération…' : chapters.length ? 'Régénérer' : 'Générer via IA'}
            </button>
          </div>
          {chapters.length > 0 && (
            <ul className="space-y-1">
              {chapters.map((ch, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-neutral-400">
                  <span className="font-mono text-gray-400 dark:text-neutral-500 w-10 shrink-0">{ch.time}</span>
                  <span>{ch.title}</span>
                </li>
              ))}
            </ul>
          )}
          {!chapters.length && !chaptersLoading && (
            <p className="text-xs text-gray-400 dark:text-neutral-500 italic">Aucun chapitre généré. La transcription doit exister.</p>
          )}
        </div>
      )}

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving || !video?.id_video}
        className="w-full py-2.5 text-sm font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-lg disabled:opacity-50 transition-colors"
      >
        {saving ? 'Enregistrement…' : saved ? '✓ Enregistré' : 'Enregistrer les métadonnées'}
      </button>
    </div>
  );
};

// ─── Tab: Upload ──────────────────────────────────────────────────────────────

const TabUpload = ({ confEvent, contact, idEvent, idEventLigne, idVideo, onUploaded, organisationId = 1, youtubeOrgs = [], onOrgChange, lang = 'fr' }) => {
  const [youtubePlaylists, setYoutubePlaylists] = useState([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');

  useEffect(() => {
    if (!idEventLigne) { setYoutubePlaylists([]); setSelectedPlaylistId(''); return; }
    const fd = new FormData();
    fd.append('action', 'get_event_youtube_playlists');
    fd.append('id_event_ligne', idEventLigne);
    fd.append('organisation_id', organisationId);
    fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
      .then(r => r.json())
      .then(d => {
        const playlists = Array.isArray(d.playlists) ? d.playlists : [];
        setYoutubePlaylists(playlists);
        const def = playlists.find(p => p.is_default) || playlists[0];
        setSelectedPlaylistId(def ? def.youtube_playlist_id : '');
      })
      .catch(() => { setYoutubePlaylists([]); setSelectedPlaylistId(''); });
  }, [idEventLigne, organisationId]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-neutral-400">Uploadez ou remplacez la vidéo YouTube pour ce créneau.</p>
      {youtubeOrgs.length > 1 && (
        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">
            Chaîne YouTube de destination
          </label>
          <select
            value={organisationId}
            onChange={e => onOrgChange?.(Number(e.target.value))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-gray-800 dark:text-white"
          >
            {youtubeOrgs.map(org => (
              <option key={org.organisation_id} value={org.organisation_id}>
                {org.label}{org.is_default ? ' (défaut)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {youtubePlaylists.length > 1 && (
        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">
            Playlist YouTube de destination
          </label>
          <select
            value={selectedPlaylistId}
            onChange={e => setSelectedPlaylistId(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-gray-800 dark:text-white"
          >
            {youtubePlaylists.map(pl => (
              <option key={pl.id} value={pl.youtube_playlist_id}>
                {pl.label || pl.youtube_playlist_id}{pl.is_default ? ' (défaut)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <DragDropVideoUpload
        idConfEvent={confEvent?.id_conf_event}
        idContact={contact?.id_contact}
        idVideo={idVideo}
        title={`${contact?.prenom || ''} ${contact?.nom || ''}`.trim()}
        idEvent={idEvent}
        organisationId={organisationId}
        youtubePlaylistId={selectedPlaylistId}
        lang={lang}
        onUploadComplete={({ id_video, youtube_id, id_conf_event: idCE, ai }) => {
          const resolvedCE = idCE ?? confEvent?.id_conf_event;
          onUploaded(resolvedCE, id_video, youtube_id, ai);
        }}
      />
    </div>
  );
};

// ─── Tab: Liens ───────────────────────────────────────────────────────────────

const CopyField = ({ label, value, icon: Icon, onEdit, editLabel }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-semibold text-gray-700 dark:text-neutral-200 flex items-center gap-1.5">
          {Icon && <Icon size={14} className="text-gray-400 dark:text-neutral-500" />}
          {label}
        </label>
        {onEdit && (
          <button onClick={onEdit} className="text-xs text-e2o-500 hover:underline">{editLabel || 'Modifier'}</button>
        )}
      </div>
      {value ? (
        <div className="flex items-center gap-2">
          <input readOnly value={value} className="flex-1 px-3 py-2 text-xs bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg font-mono text-gray-600 dark:text-neutral-300 focus:outline-none" />
          <button onClick={handleCopy} className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-200 dark:border-neutral-700 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-800 text-gray-600 dark:text-neutral-300">
            {copied ? <CheckCircle size={13} className="text-green-500" /> : <Copy size={13} />}
            {copied ? 'Copié' : 'Copier'}
          </button>
          {value.startsWith('http') && (
            <a href={value} target="_blank" rel="noopener noreferrer"
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-e2o-500 dark:text-e2o-400 border border-e2o-200 dark:border-e2o-800 rounded-lg hover:bg-e2o-50 dark:hover:bg-e2o-950/40 transition-colors">
              <ExternalLink size={13} />

            </a>
          )}
        </div>
      ) : (
        <div className="px-3 py-2 text-xs text-gray-400 dark:text-neutral-500 bg-gray-50 dark:bg-neutral-800 border border-dashed border-gray-200 dark:border-neutral-700 rounded-lg italic">
          Non renseigné
        </div>
      )}
    </div>
  );
};

const TabLinks = ({ video, confEvent, onAddYoutubeLink, onSaveDownloadLink }) => {
  const youtubeId = video?.id_youtube;
  const youtubeUrl = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : null;
  const embedUrl = video?.embed_url;
  const downloadUrl = video?.download_url;
  const idVideo = video?.id_video;
  const idConfEvent = confEvent?.id_conf_event;

  const [ytStatus, setYtStatus] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalCopied, setApprovalCopied] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [nativeStatus, setNativeStatus] = useState(null);

  useEffect(() => {
    if (!youtubeId) return;
    const fd = new FormData();
    fd.append('action', 'get_youtube_status');
    fd.append('id_youtube', youtubeId);
    fd.append('organisation_id', 1);
    fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
      .then(r => r.json()).then(setYtStatus).catch(() => { });
  }, [youtubeId]);

  useEffect(() => {
    if (!idVideo) return;
    const fd = new FormData();
    fd.append('action', 'get_native_status');
    fd.append('id_video', idVideo);
    fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
      .then(r => r.json()).then(setNativeStatus).catch(() => { });
  }, [idVideo]);

  const handleCopyApprovalLink = async () => {
    if (!idVideo) return;
    setApprovalLoading(true);
    try {
      const fd = new FormData();
      fd.append('action', 'get_approval_token');
      fd.append('id_video', idVideo);
      fd.append('base_url', window.location.origin);
      const d = await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd }).then(r => r.json());
      if (d.approval_url) {
        await navigator.clipboard.writeText(d.approval_url);
        setApprovalCopied(true);
        setTimeout(() => setApprovalCopied(false), 3000);
      }
    } finally { setApprovalLoading(false); }
  };

  const handlePublishNow = async () => {
    if (!idVideo) return;
    setPublishLoading(true);
    try {
      const fd = new FormData();
      fd.append('action', 'approve_video');
      fd.append('id_video', idVideo);
      // Generate token then approve
      const tfd = new FormData();
      tfd.append('action', 'get_approval_token');
      tfd.append('id_video', idVideo);
      tfd.append('base_url', window.location.origin);
      const td = await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: tfd }).then(r => r.json());
      if (td.token) {
        const afd = new FormData();
        afd.append('action', 'approve_video');
        afd.append('token', td.token);
        afd.append('id_video', idVideo);
        await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: afd });
        setYtStatus(prev => ({ ...prev, privacy_status: 'public' }));
      }
    } finally { setPublishLoading(false); }
  };

  const promptYoutube = () => {
    const url = window.prompt(youtubeUrl ? 'Modifier le lien YouTube :' : 'Ajouter un lien YouTube :', youtubeUrl || '');
    if (url === null) return;
    const ytId = extractYoutubeId(url);
    if (ytId) onAddYoutubeLink(idVideo, ytId, idConfEvent);
    else if (url !== '') alert('Lien YouTube invalide.');
  };

  const promptDownload = () => {
    const url = window.prompt(downloadUrl ? 'Modifier le lien de téléchargement :' : 'Ajouter un lien de téléchargement :', downloadUrl || '');
    if (url !== null) onSaveDownloadLink(idVideo, url, idConfEvent);
  };

  return (
    <div className="space-y-5">
      <CopyField label="YouTube" value={youtubeUrl} icon={Youtube} onEdit={promptYoutube} editLabel={youtubeUrl ? 'Modifier' : 'Ajouter'} />

      {/* Statut + approbation */}
      {youtubeId && ytStatus && (
        <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Statut YouTube</span>
            {ytStatus.upload_status === 'processed' && ytStatus.privacy_status === 'public' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Public
              </span>
            )}
            {ytStatus.upload_status === 'processed' && ytStatus.privacy_status === 'unlisted' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" /> Non répertorié
              </span>
            )}
            {ytStatus.upload_status === 'processed' && ytStatus.privacy_status === 'private' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" /> Privé
              </span>
            )}
            {ytStatus.upload_status && ytStatus.upload_status !== 'processed' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-e2o-100 dark:bg-e2o-900/30 text-e2o-600 dark:text-e2o-400">
                <span className="w-1.5 h-1.5 rounded-full bg-e2o-400 animate-pulse inline-block" /> En traitement
              </span>
            )}
          </div>
          {idVideo && ytStatus.privacy_status !== 'public' && (
            <div className="flex gap-2 ">
              <button type="button" onClick={handleCopyApprovalLink} disabled={approvalLoading}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 disabled:opacity-50">
                <Copy size={11} />
                {approvalLoading ? 'Génération…' : approvalCopied ? '✓ Lien copié' : 'Lien d\'approbation'}
              </button>
              <button type="button" onClick={handlePublishNow} disabled={publishLoading}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50">
                {publishLoading ? 'Publication…' : 'Publier maintenant'}
              </button>
            </div>
          )}
        </div>
      )}
      {youtubeId && !ytStatus && idVideo && (
        <div className="hidden flex gap-2">
          <button type="button" onClick={handleCopyApprovalLink} disabled={approvalLoading}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 disabled:opacity-50">
            <Copy size={11} />
            {approvalLoading ? 'Génération…' : approvalCopied ? '✓ Lien copié' : 'Lien d\'approbation'}
          </button>
        </div>
      )}

      <CopyField label="Embed URL" value={embedUrl} />
      <CopyField
        label="Téléchargement"
        value={nativeStatus?.download_url || downloadUrl}
        icon={Download}
        onEdit={nativeStatus?.has_native ? undefined : promptDownload}
        editLabel={downloadUrl ? 'Modifier' : 'Ajouter'}
      />
      {nativeStatus?.has_native && (
        <p className={`text-xs -mt-3 ${nativeStatus.is_expired ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-neutral-500'}`}>
          {nativeStatus.is_expired
            ? `Fichier natif expiré depuis le ${nativeStatus.expires_at?.slice(0, 10)}`
            : `Fichier natif conservé jusqu'au ${nativeStatus.expires_at?.slice(0, 10)} (J-${nativeStatus.days_remaining})`}
        </p>
      )}
    </div>
  );
};

// ─── Tab: Posts réseaux ───────────────────────────────────────────────────────

const RegeneratePostsButton = ({ idVideo, onDone, label = 'Régénérer', confEventId, apiUrlTranslation, lang = 'fr' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRegenerate = async () => {
    if (!idVideo) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch speakers to enrich GPT context
      let intervenants = [];
      let animateur = null;
      if (confEventId && apiUrlTranslation) {
        const [resSpk, resAni] = await Promise.all([
          fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId} AND cf.statut!=27`),
          fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId} AND cf.statut=27`),
        ]);
        const [dataSpk, dataAni] = await Promise.all([resSpk.json(), resAni.json()]);
        const pick = c => c ? { prenom: c.prenom, nom: c.nom, societe: c.societe, fonction: c.fonction, mail: c.mail, sn_linkedin: c.sn_linkedin } : null;
        intervenants = Array.isArray(dataSpk) ? dataSpk.filter(d => d.contact).map(d => pick(d.contact)) : [];
        animateur = Array.isArray(dataAni) && dataAni[0]?.contact ? pick(dataAni[0].contact) : null;
      }

      const fd = new FormData();
      fd.append('action', 'regenerate_posts');
      fd.append('id_video', idVideo);
      fd.append('lang', lang);
      if (intervenants.length > 0) fd.append('intervenants', JSON.stringify(intervenants));
      if (animateur) fd.append('animateur', JSON.stringify(animateur));
      const res = await fetch('https://manager.event2one.com/videos/upload-to-youtube/upload_api.php', {
        method: 'POST',
        headers: { 'X-Api-Key': 'mgv_yt_upload_2026' },
        body: fd,
      });
      const data = await res.json();
      if (data.error) {
        const detail = data.gpt_error?.message || data.gpt_content?.slice(0, 120) || data.json_error || '';
        setError(`${data.error}${detail ? ` — ${detail}` : ''}`);
        return;
      }
      onDone?.(data);
    } catch { setError('Erreur réseau'); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex items-center gap-2">
      {error && <span className="text-xs text-red-500">{error}</span>}
      <button
        onClick={handleRegenerate}
        disabled={loading}
        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-e2o-200 dark:border-e2o-800 text-e2o-600 dark:text-e2o-400 hover:bg-e2o-50 dark:hover:bg-e2o-900/20 disabled:opacity-50 transition-colors"
      >
        {loading ? <SpinnerIcon size={12} className="animate-spin" /> : <Sparkles size={12} />}
        {loading ? 'Génération…' : label}
      </button>
    </div>
  );
};

const SocialDraftCard = ({ role, roleLabel, color, text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${color}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{roleLabel}</span>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/50 dark:bg-black/20"><Sparkles size={9} /> IA transcription</span>
        </div>
        <button
          onClick={handleCopy}
          className="shrink-0 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white/60 dark:bg-black/20 hover:bg-white/90 dark:hover:bg-black/40 transition-colors"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? 'Copié !' : 'Copier'}
        </button>
      </div>
      <p className="text-sm leading-relaxed whitespace-pre-line">{text?.replace(/\r\n/g, '\n')}</p>
    </div>
  );
};

const DRAFT_META = [
  { role: 'animateur', roleLabel: "🎙️ Pour l'animateur / interviewer", color: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 text-blue-900 dark:text-blue-100' },
  { role: 'organisateur', roleLabel: "🏆 Pour l'organisateur de l'événement", color: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30 text-purple-900 dark:text-purple-100' },
  { role: 'interviewe', roleLabel: "🎤 Pour l'interviewé", color: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 text-green-900 dark:text-green-100' },
];

const SOCIAL_ICONS = {
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-[#0A66C2]">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  ),
  default: <Share2 size={14} className="text-gray-400 dark:text-neutral-500" />,
};

// Fix double-encoding UTF-8 produit par utf8_encode() dans api.php ("Ã©" → "é")
// Préserve les emojis et caractères Unicode modernes
const fixMojibake = s => {
  if (!s) return s;

  // Détecte si le texte contient des emojis ou des caractères Unicode élevés (> U+1F000)
  // Si c'est le cas, on ne fait pas de décodage pour préserver les emojis
  const hasEmojisOrModernUnicode = /[\u{1F000}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(s);

  if (hasEmojisOrModernUnicode) {
    return s; // Préserve le texte avec emojis tel quel
  }

  // Applique le décodage seulement si on détecte des patterns de double-encodage classiques
  const hasMojibakePattern = /Ã[©àáâãäåæçèéêë¡¢£¤¥¦§¨ª«¬®¯°±²³´µ¶·¸¹º»¼½¾¿]/i.test(s);

  if (hasMojibakePattern) {
    try {
      return decodeURIComponent(escape(s));
    } catch {
      return s;
    }
  }

  return s; // Retourne le texte tel quel si pas de problème détecté
};

const TabPosts = ({ idVideo, video, onPostsRegenerated, confEventId, apiUrlTranslation, lang = 'fr' }) => {
  const drafts = DRAFT_META.map(d => {
    const raw = d.role === 'animateur' ? video?.linkedin_post_animateur
      : d.role === 'organisateur' ? video?.linkedin_post_organisateur
        : video?.linkedin_post_interviewe;
    return raw?.trim() ? { ...d, text: fixMojibake(raw) } : null;
  }).filter(Boolean);

  const hasPosts = drafts.length > 0;
  const hasTranscription = !!video?.ia_transcription;
  const [posts, setPosts] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [addError, setAddError] = useState(null);

  const fetchPosts = useCallback(() => {
    if (!idVideo) return;
    fetch(`${API_URL}?action=getVideosPosts&params=WHERE id_video=${idVideo}`)
      .then(r => r.json())
      .then(data => Array.isArray(data) ? setPosts(data) : setPosts([]));
  }, [idVideo]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleAdd = async () => {
    const url = newUrl.trim();
    setAddError(null);
    if (!url) return;
    if (!idVideo) { setAddError('Identifiant vidéo manquant.'); return; }
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}?action=createVideosPosts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_video: idVideo, url }),
      });
      const data = await res.json();
      console.log('[createVideosPosts] response:', data);
      if (data?.id_video_post) {
        setPosts(p => [data, ...p]);
        setNewUrl('');
        setShowInput(false);
      } else if (res.ok) {
        // API succeeded but returned unexpected format — reload from server
        fetchPosts();
        setNewUrl('');
        setShowInput(false);
      } else {
        setAddError(data?.error || data?.message || 'Erreur lors de l\'ajout.');
      }
    } catch (e) {
      console.error('[createVideosPosts] error:', e);
      setAddError('Erreur réseau.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id_video_post) => {
    await fetch(`${API_URL}?action=deleteVideosPosts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_video_post }),
    });
    setPosts(p => p.filter(x => x.id_video_post !== id_video_post));
  };

  const getIcon = (url) => {
    if (url?.includes('linkedin.com')) return SOCIAL_ICONS.linkedin;
    return SOCIAL_ICONS.default;
  };

  return (
    <div className="space-y-6">

      {/* Brouillons IA */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-e2o-500" />
            <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Brouillons IA — à copier et adapter</p>
          </div>
          {hasPosts && hasTranscription && (
            <RegeneratePostsButton idVideo={idVideo} onDone={data => onPostsRegenerated?.(data)} confEventId={confEventId} apiUrlTranslation={apiUrlTranslation} lang={lang} />
          )}
        </div>

        {hasPosts ? (
          drafts.map(d => <SocialDraftCard key={d.role} {...d} />)
        ) : hasTranscription ? (
          <div className="flex flex-col items-center gap-3 py-8 rounded-xl border border-dashed border-e2o-200 dark:border-e2o-800">
            <Sparkles size={20} className="text-e2o-400" />
            <p className="text-sm text-gray-500 dark:text-neutral-400 text-center">Aucun brouillon généré pour cette vidéo.</p>
            <RegeneratePostsButton idVideo={idVideo} label="Générer les posts IA" onDone={data => onPostsRegenerated?.(data)} confEventId={confEventId} apiUrlTranslation={apiUrlTranslation} lang={lang} />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-8 rounded-xl border border-dashed border-gray-200 dark:border-neutral-700">
            <p className="text-sm text-gray-400 dark:text-neutral-500 text-center">Uploadez la vidéo sur YouTube pour activer la génération IA des posts.</p>
          </div>
        )}
      </div>

      <hr className="border-gray-100 dark:border-neutral-800" />

      {/* Posts publiés */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-neutral-400">{posts.length} post{posts.length !== 1 ? 's' : ''} enregistré{posts.length !== 1 ? 's' : ''}</p>
          {idVideo ? (
            <button onClick={() => setShowInput(v => !v)} className="flex items-center gap-1.5 text-sm text-e2o-500 hover:underline">
              <Plus size={14} /> Ajouter un post
            </button>
          ) : (
            <span className="text-xs text-gray-400 dark:text-neutral-500 italic">Vidéo requise pour ajouter des posts</span>
          )}
        </div>

        {showInput && (
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-2">
              <input
                type="text"
                value={newUrl}
                onChange={e => { setNewUrl(e.target.value); setAddError(null); }}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder="https://www.linkedin.com/posts/…"
                autoFocus
                className="flex-1 border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-e2o-400 placeholder:text-gray-400 dark:placeholder:text-neutral-500"
              />
              <button onClick={handleAdd} disabled={adding || !newUrl.trim()}
                className="shrink-0 px-4 py-2.5 text-sm font-medium text-white bg-e2o-500 rounded-lg hover:bg-e2o-600 disabled:opacity-50">
                {adding ? '…' : 'Ajouter'}
              </button>
            </div>
            {addError && <p className="text-xs text-red-500">{addError}</p>}
          </div>
        )}

        {posts.length > 0 ? (
          <ul className="divide-y divide-gray-100 dark:divide-neutral-800 rounded-lg border border-gray-100 dark:border-neutral-800 overflow-hidden">
            {posts.map(post => (
              <li key={post.id_video_post} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-neutral-800 group">
                {getIcon(post.url)}
                <a href={post.url} target="_blank" rel="noopener noreferrer"
                  className="flex-1 text-sm text-e2o-500 hover:underline truncate min-w-0">
                  {post.url}
                </a>
                <button onClick={() => handleDelete(post.id_video_post)}
                  className="opacity-0 group-hover:opacity-100 text-gray-300 dark:text-neutral-600 hover:text-red-500 transition-opacity shrink-0">
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          !showInput && <p className="text-sm text-gray-400 dark:text-neutral-500 italic text-center py-6">Aucun post enregistré</p>
        )}
      </div>
    </div>
  );
};

// ─── Tab: Intervenants ────────────────────────────────────────────────────────

const CONTACT_FIELDS = [
  { key: 'prenom', label: 'Prénom', required: true },
  { key: 'nom', label: 'Nom', required: true },
  { key: 'societe', label: 'Société' },
  { key: 'fonction', label: 'Fonction' },
  { key: 'mail', label: 'Email', type: 'email' },
  { key: 'port', label: 'Mobile' },
  { key: 'sn_linkedin', label: 'Profil LinkedIn' },
  { key: 'web', label: 'Site web' },
];

const VisuelField = ({ label, fieldName, idContact, currentValue, onUploaded }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentValue || '');
  const imgSrc = preview ? (preview.startsWith('http') ? preview : DIR_IMG_UPLOADED_CONTACTS + preview) : null;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('action', 'updateContactVisuel');
      fd.append('id_contact', idContact);
      fd.append(fieldName, file);
      const res = await fetch(`${API_URL}?action=updateContactVisuel`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.fileName) { setPreview(data.fileName); onUploaded(fieldName, data.fileName); }
    } finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1.5">{label}</label>
      <div className="flex items-center gap-3">

        {imgSrc
          ? <img src={imgSrc} alt={label} className="w-14 h-14 rounded-lg object-cover border border-gray-200 dark:border-neutral-700" />
          : <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 flex items-center justify-center"><Image size={20} className="text-gray-300" /></div>
        }
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="px-3 py-1.5 text-xs text-gray-700 dark:text-neutral-300 border border-gray-200 dark:border-neutral-700 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-700 disabled:opacity-50">
          {uploading ? 'Upload…' : 'Changer'}
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>
    </div>
  );
};

const ContactEditForm = ({ contact, onContactSaved, emptyLabel = 'Aucun contact associé.' }) => {
  const [form, setForm] = useState(Object.fromEntries(CONTACT_FIELDS.map(f => [f.key, contact?.[f.key] || ''])));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(Object.fromEntries(CONTACT_FIELDS.map(f => [f.key, contact?.[f.key] || ''])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id_contact]);

  const handleSave = async () => {
    if (!contact?.id_contact) return;
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}?action=updateContact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formData: { id_contact: contact.id_contact, ...form } }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onContactSaved?.({ ...contact, ...form });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleLinkedinSearch = () => {
    const q = [form.prenom, form.nom, form.societe].filter(Boolean).join(' ');
    if (q) window.open(`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(q)}`, '_blank', 'noopener');
  };

  if (!contact?.id_contact) return <p className="text-sm text-gray-400 dark:text-neutral-500 italic">{emptyLabel}</p>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <VisuelField label="Photo" fieldName="photo" idContact={contact.id_contact}
          currentValue={contact.photos?.small || contact.photos?.tiny || contact.photo}
          onUploaded={(k, v) => setForm(f => ({ ...f, [k]: v }))} />

        <VisuelField label="Logo société" fieldName="logo" idContact={contact.id_contact}
          currentValue={contact.logos?.small || contact.logos?.tiny}
          onUploaded={(k, v) => setForm(f => ({ ...f, [k]: v }))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {CONTACT_FIELDS.map(({ key, label, type }) => (
          <div key={key} className={key === 'sn_linkedin' || key === 'web' ? 'col-span-2' : ''}>
            <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">{label}</label>
            {key === 'sn_linkedin' ? (
              <div className="flex gap-2">
                <input type="text" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder="https://www.linkedin.com/in/…"
                  className="flex-1 border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-e2o-400" />
                {!form[key] && (
                  <button type="button" onClick={handleLinkedinSearch}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#0A66C2] rounded-lg hover:bg-[#004182]">
                    <Search size={12} /> Rechercher
                  </button>
                )}
              </div>
            ) :

              key === 'port' ? (
                <PhoneInputField
                  name="port"
                  id="port"
                  value={form.port}
                  onChange={value => setForm(f => ({ ...f, port: value }))}
                />
              ) :
                (
                  <input type={type || 'text'} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    className="w-full border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-e2o-400" />
                )}
          </div>
        ))}
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <button onClick={handleSave} disabled={saving}
        className="w-full py-2.5 text-sm font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-lg disabled:opacity-50 transition-colors">
        {saving ? 'Enregistrement…' : saved ? '✓ Enregistré' : 'Enregistrer les informations'}
      </button>
    </div>
  );
};

const ContactSearch = ({ apiUrlTranslation, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newContact, setNewContact] = useState({ prenom: '', nom: '', mail: '', societe: '', fonction: '', linkedin: '' });
  const searchRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${apiUrlTranslation}?action=getContactList&params= WHERE nom LIKE '%25${encodeURIComponent(q)}%25' OR prenom LIKE '%25${encodeURIComponent(q)}%25' OR mail LIKE '%25${encodeURIComponent(q)}%25'   LIMIT 10`);
      const data = await res.json();
      setResults(Array.isArray(data) ? data.slice(0, 8) : []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, [apiUrlTranslation]);

  useEffect(() => {
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => doSearch(query), 350);
    return () => clearTimeout(searchRef.current);
  }, [query, doSearch]);

  const handleOpenCreate = () => {
    const parts = query.trim().split(' ');
    setNewContact({ prenom: parts[0] || '', nom: parts.slice(1).join(' ') || '', mail: '', societe: '', fonction: '', linkedin: '' });
    setCreateError('');
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!newContact.prenom.trim() || !newContact.nom.trim()) {
      setCreateError('Prénom et nom requis.');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const res = await fetch(`${API_URL}?action=createContact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newContact),
      });
      const raw = await res.text();
      const data = JSON.parse(raw);
      const id_contact = typeof data === 'string' || typeof data === 'number'
        ? parseInt(data, 10)
        : data?.id_contact;
      if (!id_contact) throw new Error('Création échouée');
      onSelect({ ...newContact, id_contact });
      setQuery('');
      setResults([]);
      setShowCreate(false);
      setNewContact({ prenom: '', nom: '', mail: '', societe: '', fonction: '', linkedin: '' });
    } catch (e) {
      setCreateError('Erreur lors de la création. Réessayez.');
    } finally {
      setCreating(false);
    }
  };

  const showNoResults = !searching && query.trim().length >= 2 && results.length === 0;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setShowCreate(false); }}
          placeholder="Rechercher par nom, prénom ou email…"
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-e2o-400"
        />
        {searching && <SpinnerIcon size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-pulse" />}
      </div>

      {results.length > 0 && (
        <ul className="border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-neutral-800">
          {results.map(c => (
            <li key={c.id_contact}>
              <button type="button" onClick={() => { onSelect(c); setQuery(''); setResults([]); }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-neutral-800 text-left transition-colors">
                {c.photo
                  ? <img src={DIR_IMG_UPLOADED_CONTACTS + c.photo} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  : <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-neutral-700 flex items-center justify-center shrink-0"><User size={14} className="text-gray-400" /></div>
                }
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{c.prenom} {c.nom}</p>
                    {String(c.certified) === '1' && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400">
                        <Check size={9} /> Certifié
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 dark:text-neutral-500 truncate">{c.societe}{c.mail ? ` · ${c.mail}` : ''}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showNoResults && !showCreate && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-800/50">
          <p className="text-xs text-gray-500 dark:text-neutral-400">Aucun résultat pour « {query} »</p>
          <button type="button" onClick={handleOpenCreate}
            className="flex items-center gap-1 text-xs font-medium text-e2o-600 dark:text-e2o-400 hover:underline">
            <Plus size={12} /> Créer ce contact
          </button>
        </div>
      )}

      {showCreate && (
        <div className="rounded-lg border border-e2o-200 dark:border-e2o-800 bg-e2o-50 dark:bg-e2o-900/20 p-3 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-e2o-700 dark:text-e2o-300">Nouveau contact</p>
            <button type="button" onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X size={13} /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="text" placeholder="Prénom *" value={newContact.prenom}
              onChange={e => setNewContact(p => ({ ...p, prenom: e.target.value }))}
              className="col-span-1 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-e2o-400" />
            <input type="text" placeholder="Nom *" value={newContact.nom}
              onChange={e => setNewContact(p => ({ ...p, nom: e.target.value }))}
              className="col-span-1 px-2.5 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-e2o-400" />
          </div>
          <input type="email" placeholder="Email" value={newContact.mail}
            onChange={e => setNewContact(p => ({ ...p, mail: e.target.value }))}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-e2o-400" />
          <input type="text" placeholder="Société" value={newContact.societe}
            onChange={e => setNewContact(p => ({ ...p, societe: e.target.value }))}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-e2o-400" />
          <input type="text" placeholder="Fonction" value={newContact.fonction}
            onChange={e => setNewContact(p => ({ ...p, fonction: e.target.value }))}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-e2o-400" />
          <input type="url" placeholder="Lien LinkedIn (https://linkedin.com/in/…)" value={newContact.linkedin}
            onChange={e => setNewContact(p => ({ ...p, linkedin: e.target.value }))}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-e2o-400" />
          {createError && <p className="text-xs text-red-500">{createError}</p>}
          <button type="button" onClick={handleCreate} disabled={creating}
            className="w-full py-2 text-sm font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
            {creating ? <><SpinnerIcon size={13} className="animate-pulse" /> Création…</> : <><Plus size={13} /> Créer et ajouter</>}
          </button>
        </div>
      )}
    </div>
  );
};

const SpeakerCard = ({ c, onUpdate, onRemove, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const cardRef = useRef(null);
  useEffect(() => {
    if (defaultExpanded) {
      setExpanded(true);
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [defaultExpanded]);
  return (
    <div ref={cardRef} className="rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden">
      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-neutral-800">
        <div className="flex items-center gap-3">
          {c.photos?.tiny
            ? <img src={c.photos.tiny} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
            : <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-neutral-700 flex items-center justify-center shrink-0"><User size={14} className="text-gray-400" /></div>
          }
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{c.prenom} {c.nom}</p>
            <p className="text-xs text-gray-400 dark:text-neutral-500 truncate">{c.societe}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setExpanded(e => !e)}
            className="text-xs text-e2o-600 dark:text-e2o-400 px-2 py-1 rounded hover:bg-e2o-50 dark:hover:bg-e2o-900/20 transition-colors">
            {expanded ? 'Fermer' : 'Éditer'}
          </button>
          <button onClick={onRemove}
            className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            Retirer
          </button>
        </div>
      </div>
      {expanded && (
        <div className="p-3 border-t border-gray-200 dark:border-neutral-700">
          <ContactEditForm
            contact={c}
            onContactSaved={updated => onUpdate(updated)}
            emptyLabel=""
          />
        </div>
      )}
    </div>
  );
};

const TabSpeakers = ({ contact, onContactSaved, confEventId, idEvent, apiUrlTranslation, onParticipantAdded, defaultExpandId }) => {
  const [role, setRole] = useState('speaker');

  // ── Intervenants (speakers) ──────────────────────────────────────────────
  const [intervenants, setIntervenants] = useState(null);
  const [loadingIntervenants, setLoadingIntervenants] = useState(false);
  const [addingSpeaker, setAddingSpeaker] = useState(false);
  const [addSpeakerError, setAddSpeakerError] = useState('');
  const [showSpeakerSearch, setShowSpeakerSearch] = useState(false);

  // ── Animateur ────────────────────────────────────────────────────────────
  // animateur = { contact, id_conferencier } | null
  const [animateur, setAnimateur] = useState(null);
  const [loadingAnimateur, setLoadingAnimateur] = useState(false);
  const [creatingAnimateur, setCreatingAnimateur] = useState(false);
  const [createError, setCreateError] = useState('');

  // Auto-switch to correct sub-tab once data is loaded
  useEffect(() => {
    if (!defaultExpandId || animateur === null || intervenants === null) return;
    if (String(animateur?.contact?.id_contact) === String(defaultExpandId)) {
      setRole('animateur');
    } else {
      setRole('speaker');
    }
  }, [defaultExpandId, animateur, intervenants]);

  // Fetch all intervenants (statut != 27) — stores { contact, id_conferencier }
  const fetchIntervenants = useCallback(() => {
    if (!confEventId) return;
    setLoadingIntervenants(true);
    fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId} AND cf.statut!=27`)
      .then(r => r.json())
      .then(data => {
        setIntervenants(Array.isArray(data) ? data.filter(d => d.contact).map(d => ({ contact: d.contact, id_conferencier: d.id_conferencier })) : []);
      })
      .catch(() => setIntervenants([]))
      .finally(() => setLoadingIntervenants(false));
  }, [confEventId, apiUrlTranslation]);

  // Fetch animateur (statut=27) — stores { contact, id_conferencier }
  const fetchAnimateur = useCallback(() => {
    if (!confEventId) return;
    setLoadingAnimateur(true);
    fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId} AND cf.statut=27`)
      .then(r => r.json())
      .then(data => {
        const found = Array.isArray(data) && data.length > 0 ? { contact: data[0].contact, id_conferencier: data[0].id_conferencier } : null;
        setAnimateur(found);
      })
      .catch(() => setAnimateur(null))
      .finally(() => setLoadingAnimateur(false));
  }, [confEventId, apiUrlTranslation]);

  useEffect(() => { fetchAnimateur(); }, [fetchAnimateur]);

  useEffect(() => {
    fetchIntervenants();
  }, [fetchIntervenants]);

  const handleAddSpeaker = async (selectedContact) => {
    if (!confEventId || !selectedContact?.id_contact) return;
    setAddingSpeaker(true);
    setAddSpeakerError('');
    try {
      const res = await fetch(`${API_URL}?action=createConferencier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_conf_event: confEventId,
          id_contact: selectedContact.id_contact,
          id_event: idEvent,
          statut: 143,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowSpeakerSearch(false);
      fetchIntervenants();
      onParticipantAdded?.();
    } catch (e) {
      setAddSpeakerError(`Erreur : ${e.message}`);
    } finally {
      setAddingSpeaker(false);
    }
  };

  const handleSelectAnimateur = async (selectedContact) => {
    if (!confEventId || !selectedContact?.id_contact) return;
    setCreatingAnimateur(true);
    setCreateError('');
    try {
      const res = await fetch(`${API_URL}?action=createConferencier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_conf_event: confEventId,
          id_contact: selectedContact.id_contact,
          id_event: idEvent,
          statut: 27,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchAnimateur();
      onParticipantAdded?.();
    } catch (e) {
      setCreateError(`Erreur lors de la création : ${e.message}`);
    } finally {
      setCreatingAnimateur(false);
    }
  };

  const handleRemoveAnimateur = async (idConferencier) => {
    if (!idConferencier) return;
    try {
      await fetch(`${API_URL}?action=deleteConferencier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_conferencier: idConferencier }),
      });
      setAnimateur(null);
      onParticipantAdded?.();
    } catch (e) { console.error('deleteConferencier animateur:', e); }
  };

  const handleRemoveSpeaker = async (idConferencier) => {
    if (!idConferencier) return;
    try {
      await fetch(`${API_URL}?action=deleteConferencier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_conferencier: idConferencier }),
      });
      setIntervenants(prev => (prev || []).filter(i => i.id_conferencier !== idConferencier));
      onParticipantAdded?.();
    } catch (e) { console.error('deleteConferencier speaker:', e); }
  };

  // Speakers to display: main contact first, then additional from list (dedup)
  // Each item: { contact, id_conferencier }
  const allSpeakers = (() => {
    const list = intervenants || [];
    if (!contact?.id_contact) return list;
    const alreadyIn = list.some(i => i.contact?.id_contact === contact.id_contact);
    return alreadyIn ? list : [{ contact, id_conferencier: null }, ...list];
  })();

  return (
    <div className="space-y-4">
      {/* Sélecteur de rôle */}
      <div className="flex rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden text-sm font-medium">
        <button onClick={() => setRole('speaker')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors ${role === 'speaker' ? 'bg-e2o-500 text-white' : 'bg-white dark:bg-neutral-900 text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}>
          <User size={14} /> Intervenants
          {allSpeakers.length > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${role === 'speaker' ? 'bg-white/20 text-white' : 'bg-e2o-100 dark:bg-e2o-900/40 text-e2o-700 dark:text-e2o-300'}`}>
              {allSpeakers.length}
            </span>
          )}
        </button>
        <button onClick={() => setRole('animateur')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors border-l border-gray-200 dark:border-neutral-700 ${role === 'animateur' ? 'bg-e2o-500 text-white' : 'bg-white dark:bg-neutral-900 text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}>
          🎙️ Animateur
          {animateur && <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />}
        </button>
      </div>

      {/* ── Intervenants ─────────────────────────────────────────────────── */}
      {role === 'speaker' && (
        <div className="space-y-3">
          {loadingIntervenants && (
            <p className="text-sm text-gray-400 dark:text-neutral-500 flex items-center gap-2">
              <SpinnerIcon size={14} className="animate-pulse" /> Chargement…
            </p>
          )}

          {!loadingIntervenants && allSpeakers.length === 0 && (
            <p className="text-xs text-gray-500 dark:text-neutral-400">Aucun intervenant associé à ce créneau.</p>
          )}

          {allSpeakers.map(sp => (
            <SpeakerCard
              key={sp.contact?.id_contact || sp.id_conferencier}
              c={sp.contact}
              defaultExpanded={sp.contact?.id_contact === defaultExpandId}
              onUpdate={updated => {
                if (sp.contact?.id_contact === contact?.id_contact) onContactSaved(updated);
                setIntervenants(prev => (prev || []).map(i => i.id_conferencier === sp.id_conferencier ? { ...i, contact: { ...i.contact, ...updated } } : i));
              }}
              onRemove={() => handleRemoveSpeaker(sp.id_conferencier)}
            />
          ))}

          {/* Ajouter un intervenant */}
          <div className="pt-1">
            {!showSpeakerSearch ? (
              <button onClick={() => setShowSpeakerSearch(true)}
                className="flex items-center gap-2 text-sm text-e2o-600 dark:text-e2o-400 hover:text-e2o-700 dark:hover:text-e2o-300 transition-colors py-1">
                <Plus size={15} /> Ajouter un intervenant
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600 dark:text-neutral-400">Rechercher un contact</span>
                  <button onClick={() => { setShowSpeakerSearch(false); setAddSpeakerError(''); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-neutral-300 transition-colors">
                    <X size={14} />
                  </button>
                </div>
                <ContactSearch apiUrlTranslation={apiUrlTranslation} onSelect={handleAddSpeaker} />
                {addingSpeaker && <p className="text-xs text-gray-400 flex items-center gap-1"><SpinnerIcon size={12} className="animate-spin" /> Ajout en cours…</p>}
                {addSpeakerError && <p className="text-xs text-red-500">{addSpeakerError}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Animateur ────────────────────────────────────────────────────── */}
      {role === 'animateur' && (
        <div className="space-y-4">
          {loadingAnimateur && (
            <p className="text-sm text-gray-400 dark:text-neutral-500 flex items-center gap-2">
              <SpinnerIcon size={14} className="animate-pulse" /> Chargement…
            </p>
          )}
          {!loadingAnimateur && animateur ? (
            <SpeakerCard
              key={animateur.contact?.id_contact}
              c={animateur.contact}
              defaultExpanded={animateur.contact?.id_contact === defaultExpandId}
              onUpdate={updated => setAnimateur(prev => ({ ...prev, contact: { ...prev.contact, ...updated } }))}
              onRemove={() => handleRemoveAnimateur(animateur.id_conferencier)}
            />
          ) : !loadingAnimateur && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 dark:text-neutral-400">Aucun animateur (statut 27) trouvé pour ce créneau. Associer un contact existant :</p>
              <ContactSearch apiUrlTranslation={apiUrlTranslation} onSelect={handleSelectAnimateur} />
              {creatingAnimateur && <p className="text-xs text-gray-400 flex items-center gap-1"><SpinnerIcon size={12} className="animate-spin" /> Création en cours…</p>}
              {createError && <p className="text-xs text-red-500">{createError}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Tab: Livraison ───────────────────────────────────────────────────────────

// Design tokens partagés entre les différents templates d'e-mail (shadcn/zinc)
const EMAIL_C = {
  bg: '#fafafa',   // zinc-50
  card: '#ffffff',
  border: '#e4e4e7',   // zinc-200
  fg: '#09090b',   // zinc-950
  muted: '#f4f4f5',   // zinc-100
  mutedFg: '#71717a',   // zinc-500
  primary: '#00C9A7',
  primaryFg: '#ffffff',
  accent: '#7c3aed',   // violet-700
  accentBg: '#efefef',   // gray-100 (AI block)
  accentFg: '#09090b',   // zinc-950
  linkedin: '#0a66c2',
};
const EMAIL_F = 'system-ui,-apple-system,Segoe UI,Arial,sans-serif';
const EMAIL_MONO = 'ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace';

// opts.nested : rendu allégé (petit cercle contour) utilisé pour les sous-étapes d'un chapitre,
// afin de bien les distinguer visuellement des badges de chapitre (voir emailSectionHeader).
const emailStep = (num, title, body, opts = {}) => {
  const nested = !!opts.nested;
  const badgeSize = nested ? '18px' : '24px';
  return `
    <tr><td style="padding:${nested ? '5px 0 5px 18px' : '6px 0'};">
      <table width="100%" cellpadding="0" cellspacing="0"
        style="border:1px solid ${EMAIL_C.border};border-radius:8px;background:${EMAIL_C.card};">
        <tr><td style="padding:${nested ? '13px 18px' : '16px 20px'};font-family:${EMAIL_F};">
          <table cellpadding="0" cellspacing="0"><tr>
            <td style="vertical-align:top;padding-right:12px;">
              <span style="display:inline-block;width:${badgeSize};height:${badgeSize};line-height:${badgeSize};text-align:center;
                border-radius:999px;background:${nested ? 'transparent' : EMAIL_C.fg};
                border:${nested ? `1.5px solid ${EMAIL_C.mutedFg}` : 'none'};
                color:${nested ? EMAIL_C.mutedFg : EMAIL_C.card};font-size:${nested ? '9px' : '11px'};font-weight:700;">${num}</span>
            </td>
            <td>
              <p style="margin:0 0 6px;font-size:13px;font-weight:600;color:${EMAIL_C.fg};">${title}</p>
              <div style="font-size:13px;color:${EMAIL_C.mutedFg};line-height:1.6;">${body}</div>
            </td>
          </tr></table>
        </td></tr>
      </table>
    </td></tr>`;
};

// opts.first : supprime la marge/bordure de séparation au-dessus du tout premier chapitre.
const emailSectionHeader = (roman, title, opts = {}) => `
    <tr><td style="padding:${opts.first ? '0' : '28px'} 0 14px;${opts.first ? '' : `border-top:1px solid ${EMAIL_C.border};`}">
      <table cellpadding="0" cellspacing="0" style="${opts.first ? '' : 'margin-top:16px;'}"><tr>
        <td style="vertical-align:middle;padding-right:12px;">
          <span style="display:inline-block;width:30px;height:30px;line-height:30px;text-align:center;
            border-radius:999px;background:${EMAIL_C.fg};color:${EMAIL_C.card};font-size:13px;font-weight:800;font-family:${EMAIL_MONO};">${roman}</span>
        </td>
        <td><p style="margin:0;font-size:16px;font-weight:800;color:${EMAIL_C.fg};letter-spacing:-0.3px;">${title}</p></td>
      </tr></table>
    </td></tr>`;

export const buildDeliveryMessage = ({ contact, video, presta, currentEvent, linkedinCommunityLink, hashtag, nativeDownloadUrl, lang = 'fr' }) => {
  const youtubeId = video?.id_youtube;
  const youtubeUrl = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : '[lien vidéo à compléter]';
  const linkedinPost = buildLinkedInPost({ contact, video, presta, currentEvent, lang });
  return buildEmailMessage({ contact, currentEvent, videoLink: youtubeUrl, linkedinCommunityLink, hashtag, linkedinPost, nativeDownloadUrl, lang });
};

const buildLinkedInPost = ({ contact, video, presta, currentEvent, lang = 'fr' }) => {
  const societe = contact?.societe || '';
  const eventName = currentEvent?.nom || '';
  const youtubeId = video?.id_youtube;
  const youtubeUrl = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : (lang === 'en' ? '[video link to complete]' : '[lien vidéo à compléter]');
  const punchline = presta?.punchline || video?.titre || '';
  const description = video?.description || '';

  // Première phrase de la description pour le corps du post
  const firstSentences = description
    ? description.split(/(?<=[.!?])\s+/).slice(0, 2).join(' ')
    : '';

  // Hashtag à partir du nom d'événement
  const eventHashtag = '#' + eventName.replace(/[^a-zA-Z0-9\u00C0-\u017E]/g, '');
  const societeHashtag = societe ? '#' + societe.replace(/[^a-zA-Z0-9\u00C0-\u017E]/g, '') : '';

  if (lang === 'en') {
    return [
      `🎙️ I had the pleasure of being interviewed at ${eventName}${societe ? ` as a representative of ${societe}` : ''}!`,
      '',
      punchline ? `During this exchange, I had the opportunity to share my view on: ${punchline}` : '',
      '',
      firstSentences || `We discussed the key challenges of our industry and the solutions ${societe || 'our team'} is deploying to address them.`,
      '',
      `Watch the full interview here 👇`,
      `${youtubeUrl}`,
      '',
      `${eventHashtag}${societeHashtag ? ' ' + societeHashtag : ''}`,
    ].filter(l => l !== null).join('\n');
  }

  return [
    `🎙️ J'ai eu le plaisir d'être interviewé lors de ${eventName} ${societe ? ` en tant que représentant de ${societe}` : ''} !`,
    '',
    punchline ? `Au cours de cet échange, j'ai eu l'occasion de partager ma vision sur : ${punchline}` : '',
    '',
    firstSentences || `Nous avons abordé les enjeux clés de notre secteur et les solutions que ${societe || 'notre équipe'} déploie pour y répondre.`,
    '',
    `Retrouvez l'interview complète ici 👇`,
    `${youtubeUrl}`,
    '',
    `${eventHashtag}${societeHashtag ? ' ' + societeHashtag : ''}`,
  ].filter(l => l !== null).join('\n');
};

const formatRelativeDate = (iso) => {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `Il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const EMAIL_T = {
  fr: {
    heroTitle: 'Votre interview<br>est en ligne',
    greeting: (prenom, nom) => `Bonjour <strong>${prenom} ${nom}</strong>,`,
    intro: (eventName) => `Merci pour votre participation lors de <strong style="color:${EMAIL_C.fg};">${eventName}</strong>. Votre interview est désormais disponible — partagez-la pour maximiser votre visibilité.`,
    ctaViewInterview: 'Voir mon interview →',
    stepsHeader: 'Maximisez la visibilité de votre vidéo en 3 étapes simples',
    subscribeChannel: 'Abonnez-vous à notre chaîne YouTube',
    sharePost: 'Partagez votre interview sur LinkedIn',
    sharePostFallback: 'Rédigez un post personnalisé avec le lien de votre interview.',
    aiSuggestion: '✦ Suggestion · À copier-coller',
    tagAccounts: 'Taguez les comptes qui relaieront votre post',
    downloadNativeTitle: 'Téléchargez votre vidéo native',
    downloadNativeBody: 'Le fichier source, idéal pour un montage personnalisé ou une publication native sur LinkedIn, Instagram ou TikTok.',
    officialLinkedin: 'Publication officielle LinkedIn',
    seeYou: 'À bientôt,',
    team: (eventName) => `L'équipe ${eventName}`,
    linkPlaceholder: '[lien à compléter]',
  },
  en: {
    heroTitle: 'Your interview<br>is now online',
    greeting: (prenom, nom) => `Hello <strong>${prenom} ${nom}</strong>,`,
    intro: (eventName) => `Thank you for taking part in <strong style="color:${EMAIL_C.fg};">${eventName}</strong>. Your interview is now available — share it to maximize your visibility.`,
    ctaViewInterview: 'Watch my interview →',
    stepsHeader: 'Maximize your video\'s visibility in 3 simple steps',
    subscribeChannel: 'Subscribe to our YouTube channel',
    sharePost: 'Share your interview on LinkedIn',
    sharePostFallback: 'Write a personalized post with the link to your interview.',
    aiSuggestion: '✦ Suggestion · Copy and paste',
    tagAccounts: 'Tag the accounts that will share your post',
    downloadNativeTitle: 'Download your native video',
    downloadNativeBody: 'The source file, ideal for a custom edit or a native post on LinkedIn, Instagram or TikTok.',
    officialLinkedin: 'Official LinkedIn post',
    seeYou: 'See you soon,',
    team: (eventName) => `The ${eventName} team`,
    linkPlaceholder: '[link to complete]',
  },
};

const buildEmailMessage = ({ contact, currentEvent, videoLink, linkedinCommunityLink, hashtag, linkedinPost, nativeDownloadUrl, lang = 'fr' }) => {
  const t = EMAIL_T[lang] || EMAIL_T.fr;
  const prenom = contact?.prenom || '';
  const nom = contact?.nom || '';
  const eventName = currentEvent?.nom || '';
  const link = videoLink || t.linkPlaceholder;
  const youtubeChannelId = currentEvent?.event_ligne?.youtube_channel_id;
  const linkedinProfilesToConnectWidth = currentEvent?.event_ligne?.linkedin_profiles_to_connect_width || '';

  // shadcn tokens (mutualisés, cf. EMAIL_C/EMAIL_F/EMAIL_MONO/emailStep)
  const C = EMAIL_C;
  const F = EMAIL_F;
  const MONO = EMAIL_MONO;
  const step = emailStep;

  const profileList = linkedinProfilesToConnectWidth
    ? linkedinProfilesToConnectWidth.split('\n').filter(l => l.trim())
      .map(l => `<li style="margin:4px 0;font-size:13px;"><a href="${l.trim()}" style="color:${C.linkedin};text-decoration:none;">${l.trim()}</a></li>`).join('')
    : '';

  const hashtagsHtml = hashtag
    ? hashtag.split(/\s+/).filter(Boolean).map(h =>
      `<span style="display:inline-block;border:1px solid ${C.border};border-radius:6px;padding:2px 8px;font-size:12px;color:${C.mutedFg};margin:2px 2px;font-family:${MONO};">${h}</span>`
    ).join('')
    : '';

  const stepN = (base) => youtubeChannelId ? base + 1 : base;

  return `<div style="background:${C.bg};padding:32px 16px;font-family:${F};">
<table width="760" cellpadding="0" cellspacing="0" align="center"
  style="max-width:760px;background:${C.card};border:1px solid ${C.border};border-radius:12px;overflow:hidden;">

  <!-- accent top bar -->
  <tr><td style="height:3px;background:${C.primary};font-size:0;line-height:0;">&nbsp;</td></tr>

  <!-- HEADER -->
  <tr><td style="padding:32px 32px 24px;border-bottom:1px solid ${C.border};">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${C.mutedFg};">${eventName}</p>
    <p style="margin:0;font-size:22px;font-weight:700;color:${C.fg};letter-spacing:-0.4px;line-height:1.2;">${t.heroTitle}</p>
  </td></tr>

  <!-- BODY -->
  <tr><td style="padding:24px 32px;">

    <p style="margin:0 0 4px;font-size:15px;color:${C.fg};">${t.greeting(prenom, nom)}</p>
    <p style="margin:0 0 24px;font-size:14px;color:${C.mutedFg};line-height:1.6;">
      ${t.intro(eventName)}
    </p>

    <!-- CTA -->
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:28px;">
      <tr><td>
        <a href="${link}"
          style="display:inline-block;background:${C.fg};color:${C.card};font-size:13px;font-weight:600;
            text-decoration:none;padding:11px 22px;border-radius:7px;letter-spacing:0.01em;">
          ${t.ctaViewInterview}
        </a>
      </td></tr>
      <tr><td style="padding-top:8px;">
        <a href="${link}" style="font-size:11px;color:${C.mutedFg};text-decoration:none;font-family:${MONO};word-break:break-all;">${link}</a>
      </td></tr>
    </table>

    <!-- divider label -->
    <p style="margin:0 0 10px;font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${C.mutedFg};">
    ${t.stepsHeader}
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:separate;border-spacing:0 6px;">

      ${youtubeChannelId ? step(1,
    t.subscribeChannel,
    `<a href="https://www.youtube.com/channel/${youtubeChannelId}"
          style="color:${C.primary};font-family:${MONO};font-size:12px;word-break:break-all;">
          youtube.com/channel/${youtubeChannelId}
        </a>`) : ''}

      ${step(stepN(1),
      t.sharePost,
      linkedinPost ? `
          <!-- AI block dark -->
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:10px;">
            <tr><td style="background:${C.accentBg};border-radius:8px;padding:14px 16px;">
              <p style="margin:0 0 10px;font-size:10px;font-weight:600;letter-spacing:0.1em;
                text-transform:uppercase;color:${C.accent};font-family:${MONO};">${t.aiSuggestion}</p>
              <p style="margin:0;font-size:13px;color:${C.accentFg};line-height:1.7;
                white-space:pre-line;font-family:${MONO};">${linkedinPost}</p>
            </td></tr>
          </table>` : t.sharePostFallback
    )}

      ${profileList ? step(stepN(2),
      t.tagAccounts,
      `<ul style="margin:6px 0 0;padding-left:16px;">${profileList}</ul>`) : ''}

    </table>

    ${nativeDownloadUrl ? `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px;">
      <tr><td style="border:1px solid ${C.border};border-radius:8px;padding:14px 16px;">
        <p style="margin:0 0 4px;font-size:12px;font-weight:600;color:${C.fg};">${t.downloadNativeTitle}</p>
        <p style="margin:0 0 8px;font-size:12px;color:${C.mutedFg};line-height:1.5;">
          ${t.downloadNativeBody}
        </p>
        <a href="${nativeDownloadUrl}" style="font-size:12px;color:${C.primary};font-family:${MONO};word-break:break-all;">${nativeDownloadUrl}</a>
      </td></tr>
    </table>` : ''}

    ${linkedinCommunityLink ? `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px;">
      <tr><td style="border:1px solid ${C.border};border-radius:8px;padding:14px 16px;">
        <p style="margin:0 0 4px;font-size:12px;font-weight:600;color:${C.fg};">${t.officialLinkedin}</p>
        <a href="${linkedinCommunityLink}" style="font-size:12px;color:${C.linkedin};font-family:${MONO};word-break:break-all;">${linkedinCommunityLink}</a>
      </td></tr>
    </table>` : ''}

    ${hashtagsHtml ? `<div style="margin-top:16px;">${hashtagsHtml}</div>` : ''}

  </td></tr>

  <!-- FOOTER -->
  <tr><td style="padding:20px 32px;border-top:1px solid ${C.border};">
    <p style="margin:0;font-size:13px;color:${C.mutedFg};">${t.seeYou}</p>
    <p style="margin:4px 0 0;font-size:13px;font-weight:600;color:${C.fg};">${t.team(eventName)}</p>
  </td></tr>

</table>
</div>`;
};

const buildOnsiteMessage = ({ contact, video, presta, currentEvent, linkedinCommunityLink, hashtag, nativeDownloadUrl, lang = 'fr' }) => {
  const youtubeId = video?.id_youtube;
  const youtubeUrl = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : '[lien vidéo à compléter]';
  const linkedinPost = buildLinkedInPost({ contact, video, presta, currentEvent, lang });
  return buildEmailMessage({ contact, currentEvent, videoLink: youtubeUrl, linkedinCommunityLink, hashtag, linkedinPost, nativeDownloadUrl, lang });
};

// Template "Diffusion complète" — 3 blocs (I. chaîne YouTube / II. vidéo native / III. relai animateur)
// animateurContact : contact de l'animateur du créneau, à passer uniquement si le destinataire n'est pas lui-même l'animateur
// (le bloc III est alors automatiquement omis).
const FULL_DELIVERY_T = {
  fr: {
    intro: (eventName) => `Merci pour votre participation lors de <strong style="color:${EMAIL_C.fg};">${eventName}</strong>. Votre interview est désormais disponible — voici comment en maximiser la visibilité.`,
    sectionITitle: (hasChannel) => `Voir mon interview en ligne${hasChannel ? ' sur la chaîne YouTube' : ''}`,
    step1Title: 'Je m\'abonne à la chaîne YouTube',
    step1Body: (youtubeChannelId) => `Afin de suivre l'actualité de notre secteur toute l'année, je m'abonne à la chaîne YouTube${youtubeChannelId ? ` : <a href="https://www.youtube.com/channel/${youtubeChannelId}" style="color:${EMAIL_C.primary};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">youtube.com/channel/${youtubeChannelId}</a>` : '.'}`,
    step2Title: 'Je partage immédiatement cette vidéo',
    step2Body: (youtubeUrl) => `Afin d'augmenter la visibilité de mon interview, je partage immédiatement cette vidéo auprès de mes contacts :<br>\n      <a href="${youtubeUrl}" style="color:${EMAIL_C.primary};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${youtubeUrl}</a>`,
    sectionIITitle: 'Récupérer la vidéo native pour mes réseaux sociaux, ma newsletter et mon site web',
    dlStep1Title: 'Je télécharge la vidéo native',
    dlStep1BodyOk: (url) => `Afin de bénéficier d'une visibilité accrue sur les réseaux sociaux, je charge la vidéo native et la recharge directement dans mes réseaux sociaux, ma newsletter et mon site web :<br>\n        <a href="${url}" style="color:${EMAIL_C.primary};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${url}</a>`,
    dlStep1BodyKo: 'Fichier natif non disponible pour le moment.',
    dlStep2Title: 'Un texte prêt à l\'emploi',
    dlStep2Intro: 'Voici un exemple de texte pouvant accompagner votre vidéo, que vous pouvez copier-coller et adapter librement :',
    aiSuggestion: '✦ Suggestion · À copier-coller',
    sectionIIITitle: 'Autoriser l\'animateur à diffuser sa vidéo sur son réseau social et son portail vidéo',
    animateurIntro: (name, valorisation) => `Votre animateur <strong style="color:${EMAIL_C.fg};">${name}</strong>${valorisation ? ` ${valorisation}` : ' est un acteur reconnu de notre secteur'}.`,
    checkProfileTitle: 'Je vérifie mon propre profil LinkedIn',
    checkProfileBody: (recipientLinkedin) => `Afin que l'animateur puisse vous taguer lors de la publication, je vérifie mon propre profil LinkedIn${recipientLinkedin ? ` : <a href="${recipientLinkedin}" style="color:${EMAIL_C.linkedin};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${recipientLinkedin}</a>` : '.'}`,
    connectTitle: 'Je me connecte avec mon animateur sur LinkedIn',
    connectBody: (animateurName, animateurLinkedin) => `Afin que le taux de diffusion du post de l'animateur soit optimal, je me connecte avec ${animateurName || 'mon animateur'} sur LinkedIn${animateurLinkedin ? ` : <a href="${animateurLinkedin}" style="color:${EMAIL_C.linkedin};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${animateurLinkedin}</a>` : '.'}`,
  },
  en: {
    intro: (eventName) => `Thank you for taking part in <strong style="color:${EMAIL_C.fg};">${eventName}</strong>. Your interview is now available — here's how to maximize its visibility.`,
    sectionITitle: (hasChannel) => `Watch my interview online${hasChannel ? ' on the YouTube channel' : ''}`,
    step1Title: 'I subscribe to the YouTube channel',
    step1Body: (youtubeChannelId) => `To follow our industry's news all year long, I subscribe to the YouTube channel${youtubeChannelId ? ` : <a href="https://www.youtube.com/channel/${youtubeChannelId}" style="color:${EMAIL_C.primary};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">youtube.com/channel/${youtubeChannelId}</a>` : '.'}`,
    step2Title: 'I immediately share this video',
    step2Body: (youtubeUrl) => `To increase my interview's visibility, I immediately share this video with my contacts:<br>\n      <a href="${youtubeUrl}" style="color:${EMAIL_C.primary};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${youtubeUrl}</a>`,
    sectionIITitle: 'Get the native video for my social media, newsletter and website',
    dlStep1Title: 'I download the native video',
    dlStep1BodyOk: (url) => `To benefit from increased visibility on social media, I download the native video and re-upload it directly to my social media, newsletter and website:<br>\n        <a href="${url}" style="color:${EMAIL_C.primary};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${url}</a>`,
    dlStep1BodyKo: 'Native file not available at this time.',
    dlStep2Title: 'Ready-to-use text',
    dlStep2Intro: 'Here is an example text you can copy, paste and adapt freely to accompany your video:',
    aiSuggestion: '✦ Suggestion · Copy and paste',
    sectionIIITitle: 'Allow the moderator to share their video on their social network and video portal',
    animateurIntro: (name, valorisation) => `Your host <strong style="color:${EMAIL_C.fg};">${name}</strong>${valorisation ? ` ${valorisation}` : ' is a recognized figure in our industry'}.`,
    checkProfileTitle: 'I check my own LinkedIn profile',
    checkProfileBody: (recipientLinkedin) => `So the host can tag you when publishing, I check my own LinkedIn profile${recipientLinkedin ? ` : <a href="${recipientLinkedin}" style="color:${EMAIL_C.linkedin};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${recipientLinkedin}</a>` : '.'}`,
    connectTitle: 'I connect with my moderator on LinkedIn',
    connectBody: (animateurName, animateurLinkedin) => `For the moderator's post to reach its full potential, I connect with ${animateurName || 'my moderator'} on LinkedIn${animateurLinkedin ? ` : <a href="${animateurLinkedin}" style="color:${EMAIL_C.linkedin};font-family:${EMAIL_MONO};font-size:12px;word-break:break-all;">${animateurLinkedin}</a>` : '.'}`,
  },
};

const buildFullDeliveryMessage = ({ contact, video, presta, currentEvent, linkedinCommunityLink, hashtag, nativeDownloadUrl, animateurContact, lang = 'fr' }) => {
  const C = EMAIL_C, F = EMAIL_F, MONO = EMAIL_MONO, step = emailStep, section = emailSectionHeader;
  const t = EMAIL_T[lang] || EMAIL_T.fr;
  const ft = FULL_DELIVERY_T[lang] || FULL_DELIVERY_T.fr;

  const prenom = contact?.prenom || '';
  const nom = contact?.nom || '';
  const eventName = currentEvent?.nom || '';
  const youtubeId = video?.id_youtube;
  const youtubeUrl = youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : '[lien vidéo à compléter]';
  const youtubeChannelId = currentEvent?.event_ligne?.youtube_channel_id;
  // Même texte que l'onglet Social > Pour l'interviewé (post généré par IA), avec repli
  // sur le template générique si l'IA n'a pas encore produit de texte pour cette vidéo.
  const linkedinPost = video?.linkedin_post_interviewe?.trim()
    ? fixMojibake(video.linkedin_post_interviewe)
    : buildLinkedInPost({ contact, video, presta, currentEvent, lang });
  const recipientLinkedin = contact?.sn_linkedin || '';
  const animateurName = animateurContact ? `${animateurContact.prenom || ''} ${animateurContact.nom || ''}`.trim() : '';
  const animateurLinkedin = animateurContact?.sn_linkedin || '';
  const animateurValorisation = animateurContact?.valorisation_audience || '';

  const hashtagsHtml = hashtag
    ? hashtag.split(/\s+/).filter(Boolean).map(h =>
      `<span style="display:inline-block;border:1px solid ${C.border};border-radius:6px;padding:2px 8px;font-size:12px;color:${C.mutedFg};margin:2px 2px;font-family:${MONO};">${h}</span>`
    ).join('')
    : '';

  const blockI = `
    ${section('I', ft.sectionITitle(!!youtubeChannelId), { first: true })}
    ${step(1,
    ft.step1Title,
    ft.step1Body(youtubeChannelId),
    { nested: true })}
    ${step(2,
      ft.step2Title,
      ft.step2Body(youtubeUrl),
      { nested: true })}`;

  const blockII = `
    ${section('II', ft.sectionIITitle)}
    ${step(1,
    ft.dlStep1Title,
    nativeDownloadUrl ? ft.dlStep1BodyOk(nativeDownloadUrl) : ft.dlStep1BodyKo,
    { nested: true })}
    ${step(2,
      ft.dlStep2Title,
      `${ft.dlStep2Intro}
      <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:10px;">
        <tr><td style="background:${C.accentBg};border-radius:8px;padding:14px 16px;">
          <p style="margin:0 0 10px;font-size:10px;font-weight:600;letter-spacing:0.1em;
            text-transform:uppercase;color:${C.accent};font-family:${MONO};">${ft.aiSuggestion}</p>
          <p style="margin:0;font-size:13px;color:${C.accentFg};line-height:1.7;
            white-space:pre-line;font-family:${MONO};">${linkedinPost}</p>
        </td></tr>
      </table>`,
      { nested: true })}`;

  const blockIII = animateurContact ? `
    ${section('III', ft.sectionIIITitle)}
    <tr><td style="padding:2px 0 10px 18px;font-size:13px;color:${C.mutedFg};line-height:1.6;">
      ${ft.animateurIntro(animateurName, animateurValorisation)}
    </td></tr>
    ${step(1,
    ft.checkProfileTitle,
    ft.checkProfileBody(recipientLinkedin),
    { nested: true })}
    ${step(2,
      ft.connectTitle,
      ft.connectBody(animateurName, animateurLinkedin),
      { nested: true })}` : '';

  return `<div style="background:${C.bg};padding:32px 16px;font-family:${F};">
<table width="760" cellpadding="0" cellspacing="0" align="center"
  style="max-width:760px;background:${C.card};border:1px solid ${C.border};border-radius:12px;overflow:hidden;">

  <!-- accent top bar -->
  <tr><td style="height:3px;background:${C.primary};font-size:0;line-height:0;">&nbsp;</td></tr>

  <!-- HEADER -->
  <tr><td style="padding:32px 32px 24px;border-bottom:1px solid ${C.border};">
    <p style="margin:0 0 4px;font-size:11px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${C.mutedFg};">${eventName}</p>
    <p style="margin:0;font-size:22px;font-weight:700;color:${C.fg};letter-spacing:-0.4px;line-height:1.2;">${t.heroTitle}</p>
  </td></tr>

  <!-- BODY -->
  <tr><td style="padding:8px 32px 24px;">

    <p style="margin:24px 0 0;font-size:15px;color:${C.fg};">${t.greeting(prenom, nom)}</p>
    <p style="margin:8px 0 0;font-size:14px;color:${C.mutedFg};line-height:1.6;">
      ${ft.intro(eventName)}
    </p>

    <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:separate;border-spacing:0 6px;">
      ${blockI}
      ${blockII}
      ${blockIII}
    </table>

    ${linkedinCommunityLink ? `
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px;">
      <tr><td style="border:1px solid ${C.border};border-radius:8px;padding:14px 16px;">
        <p style="margin:0 0 4px;font-size:12px;font-weight:600;color:${C.fg};">${t.officialLinkedin}</p>
        <a href="${linkedinCommunityLink}" style="font-size:12px;color:${C.linkedin};font-family:${MONO};word-break:break-all;">${linkedinCommunityLink}</a>
      </td></tr>
    </table>` : ''}

    ${hashtagsHtml ? `<div style="margin-top:16px;">${hashtagsHtml}</div>` : ''}

  </td></tr>

  <!-- FOOTER -->
  <tr><td style="padding:20px 32px;border-top:1px solid ${C.border};">
    <p style="margin:0;font-size:13px;color:${C.mutedFg};">${t.seeYou}</p>
    <p style="margin:4px 0 0;font-size:13px;font-weight:600;color:${C.fg};">${t.team(eventName)}</p>
  </td></tr>

</table>
</div>`;
};

const TabDelivery = ({ confEventId, video, presta, idEvent, currentEvent, apiUrlTranslation }) => {
  const [phase, setPhase] = useState('complete');

  // Langue de l'e-mail de livraison = langue d'enregistrement de l'interview.
  // Utilisée uniquement pour choisir le template de message (buildXxxMessage ci-dessous) ;
  // le champ presta.langue_enregistrement peut contenir des valeurs legacy invalides
  // (ex. "interview"), donc on ne doit jamais s'en servir pour construire une URL API.
  const messageLang = presta?.langue_enregistrement || 'fr';

  // ── Participants ────────────────────────────────────────────────────────────
  const [participants, setParticipants] = useState(null);
  const [selectedConferencierId, setSelectedConferencierId] = useState(null);

  useEffect(() => {
    if (!confEventId) return;
    // Utilise l'URL API standard (langue de l'UI), pas deliveryApiUrlTranslation :
    // presta?.langue_enregistrement peut contenir une valeur legacy invalide (ex. "interview")
    // qui produirait une URL locale inexistante et ferait échouer le fetch silencieusement.
    fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId}`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setParticipants(data);
          setSelectedConferencierId(data[0].id_conferencier);
        } else {
          setParticipants([]);
        }
      })
      .catch(() => setParticipants([]));
  }, [confEventId, apiUrlTranslation]);

  const selectedParticipant = participants?.find(p => p.id_conferencier === selectedConferencierId);
  const contact = selectedParticipant?.contact || null;
  const conferencier = selectedParticipant ? { id_conferencier: selectedParticipant.id_conferencier } : null;
  const isAnimateur = String(selectedParticipant?.statut) === '27';

  // Animateur pour le bloc III du template "Diffusion complète" : d'abord celui du créneau vidéo
  // (statut 27 sur ce id_conf_event), sinon celui renseigné au niveau de l'événement (paramètres).
  // Omis si le destinataire sélectionné est lui-même l'animateur (comparaison par id_contact).
  const [eventAnimateurs, setEventAnimateurs] = useState(null);

  useEffect(() => {
    if (!idEvent) return;
    fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_event=${idEvent} AND cf.statut=27`)
      .then(r => r.json())
      .then(data => setEventAnimateurs(Array.isArray(data) ? data : []))
      .catch(() => setEventAnimateurs([]));
  }, [idEvent, apiUrlTranslation]);

  const slotAnimateurContact = participants?.find(p => String(p.statut) === '27')?.contact || null;
  const eventAnimateurContact = eventAnimateurs?.[0]?.contact || null;
  const animateurContact = slotAnimateurContact || eventAnimateurContact;
  const animateurContactForDelivery = (animateurContact && animateurContact.id_contact !== contact?.id_contact)
    ? animateurContact
    : null;

  // ── Dérivés ─────────────────────────────────────────────────────────────────
  const idConferencier = conferencier?.id_conferencier;
  const idVideo = video?.id_video;
  const youtubeId = video?.id_youtube;
  const hasEmail = !!contact?.mail;
  const hasVideo = !!(video?.embed_url || youtubeId);
  const ficheUrl = idConferencier && idVideo
    ? `/events/${idEvent}/fiche-livraison/icf/${idConferencier}/idv/${idVideo}`
    : null;

  // ── Email ────────────────────────────────────────────────────────────────────
  const [emailStatus, setEmailStatus] = useState('idle');
  const [linkedinCommunityLink, setLinkedinCommunityLink] = useState('');
  const [nativeInfo, setNativeInfo] = useState(null);
  const [includeNativeLink, setIncludeNativeLink] = useState(true);

  useEffect(() => {
    if (!idVideo) return;
    const fd = new FormData();
    fd.append('action', 'get_native_status');
    fd.append('id_video', idVideo);
    fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
      .then(r => r.json()).then(setNativeInfo).catch(() => { });
  }, [idVideo]);

  const nativeDownloadUrl = (includeNativeLink && nativeInfo?.has_native) ? nativeInfo.download_url : null;

  const hashtag = currentEvent?.event_ligne?.hashtag || '';
  const ficheFullUrl = ficheUrl ? `https://www.myglobalvillage.com${ficheUrl}` : null;
  const youtubeLink = youtubeId ? `https://youtu.be/${youtubeId}` : null;

  // type: 'onsite' (fiche) | 'postevent' (youtube)
  const handleSendEmail = async (type = 'postevent') => {
    const videoLink = type === 'onsite' ? ficheFullUrl : youtubeLink;
    if (!contact?.mail) { alert('Email du contact non renseigné.'); return; }
    if (!videoLink) { alert(type === 'onsite' ? 'Fiche de livraison non disponible.' : 'Aucune vidéo YouTube disponible.'); return; }
    setEmailStatus('sending');
    try {
      const res = await fetch(`${API_URL}?action=sendVideoLinkEmail`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: contact.mail,
          to_prenom: contact.prenom,
          to_nom: contact.nom,
          to_name: `${contact.prenom} ${contact.nom}`,
          to_body: type === 'onsite' ? onsiteMessage : type === 'complete' ? fullMessage : message,
          video_link: videoLink,
          eventName: currentEvent?.nom,
          fromName: `Studio webcast - ${currentEvent?.nom}`,
          idEvent,
          hashtag,
          linkedin_community_link: linkedinCommunityLink,
          ficheLivraisonLink: ficheFullUrl || '',
        }),
      });
      const result = await res.json();
      setEmailStatus(result.success ? 'sent' : 'error');
      if (result.success) {
        await logActivity({
          action_type: 'video_link_sent',
          id_contact_receiver: contact.id_contact,
          id_event: idEvent,
          metadata: { email: contact.mail, type, video_link: videoLink, native_link_included: !!nativeDownloadUrl },
        });
        refreshHistory();
      }
    } catch { setEmailStatus('error'); }
  };

  // Reset email status when contact changes
  useEffect(() => { setEmailStatus('idle'); }, [selectedConferencierId]);

  // ── Historique ───────────────────────────────────────────────────────────────
  const { logs: sendHistory, refresh: refreshHistory } = useActivityLog({
    id_contact_receiver: contact?.id_contact,
    id_event: idEvent,
    action_type: 'video_link_sent',
    enabled: !!contact?.id_contact && !!idEvent,
  });

  // ── Message / LinkedIn ───────────────────────────────────────────────────────
  const [onsiteMessage, setOnsiteMessage] = useState('');
  const [onsiteCopied, setOnsiteCopied] = useState(false);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [fullMessage, setFullMessage] = useState('');
  const [fullCopied, setFullCopied] = useState(false);
  const [linkedInPost, setLinkedInPost] = useState('');
  const [linkedInCopied, setLinkedInCopied] = useState(false);

  useEffect(() => {
    setOnsiteMessage(buildOnsiteMessage({ contact, video, presta, currentEvent, linkedinCommunityLink, hashtag, nativeDownloadUrl, lang: messageLang }));
    setMessage(buildDeliveryMessage({ contact, video, presta, currentEvent, linkedinCommunityLink, hashtag, nativeDownloadUrl, lang: messageLang }));
    setFullMessage(buildFullDeliveryMessage({ contact, video, presta, currentEvent, linkedinCommunityLink, hashtag, nativeDownloadUrl, animateurContact: animateurContactForDelivery, lang: messageLang }));
    setLinkedInPost(buildLinkedInPost({ contact, video, presta, currentEvent, lang: messageLang }));
    setOnsiteCopied(false);
    setCopied(false);
    setFullCopied(false);
    setLinkedInCopied(false);
  }, [contact?.id_contact, video?.id_youtube, currentEvent?.nom, linkedinCommunityLink, hashtag, nativeDownloadUrl, animateurContactForDelivery?.id_contact, messageLang]); // eslint-disable-line react-hooks/exhaustive-deps

  const btnClass = {
    sending: 'bg-yellow-500 cursor-not-allowed',
    sent: 'bg-green-600',
    error: 'bg-red-600',
  }[emailStatus] || 'bg-e2o-500 hover:bg-e2o-600';

  const btnContent = {
    sending: <><SpinnerIcon size={14} className="animate-pulse" /> Envoi…</>,
    sent: <><CheckCircle size={14} /> Envoyé !</>,
    error: <><XCircle size={14} /> Erreur</>,
  }[emailStatus] || <><Mail size={14} /> Envoyer au contact</>;

  return (
    <div className="space-y-4">

      {/* ── Sélecteur de participant ──────────────────────────────────────────── */}
      {participants === null && (
        <p className="text-sm text-gray-400 dark:text-neutral-500 flex items-center gap-2">
          <SpinnerIcon size={14} className="animate-pulse" /> Chargement des participants…
        </p>
      )}

      {participants !== null && participants.length === 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-400">Aucun participant associé à ce créneau.</p>
      )}

      {participants !== null && participants.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">Contact</p>
          <div className="flex flex-wrap gap-2">
            {participants.map(p => {
              const c = p.contact;
              const isAnim = String(p.statut) === '27';
              const isSelected = p.id_conferencier === selectedConferencierId;
              return (
                <button
                  key={p.id_conferencier}
                  onClick={() => setSelectedConferencierId(p.id_conferencier)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${isSelected
                    ? 'bg-e2o-500 border-e2o-500 text-white'
                    : 'border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}
                >
                  {c?.photos?.tiny || c?.photos?.small || c?.photo
                    ? <img src={c.photos?.tiny || c.photos?.small || (DIR_IMG_UPLOADED_CONTACTS + c.photo)} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                    : <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-neutral-700 flex items-center justify-center shrink-0"><User size={10} /></div>
                  }
                  <span className="truncate max-w-[140px]">{c?.prenom} {c?.nom}</span>
                  {isAnim && <span className="text-xs opacity-70">🎙️</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Sélecteur de phase ────────────────────────────────────────────────── */}
      {contact && (
        <>
          <div className="flex rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden text-sm font-medium">
            <button onClick={() => setPhase('complete')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors ${phase === 'complete' ? 'bg-e2o-500 text-white' : 'bg-white dark:bg-neutral-900 text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}>
              <Sparkles size={14} /> Diffusion complète
            </button>
            <button onClick={() => setPhase('onsite')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors border-l border-gray-200 dark:border-neutral-700 ${phase === 'onsite' ? 'bg-e2o-500 text-white' : 'bg-white dark:bg-neutral-900 text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}>
              <Zap size={14} /> Sur site — Jour J
            </button>
            <button onClick={() => setPhase('postevent')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors border-l border-gray-200 dark:border-neutral-700 ${phase === 'postevent' ? 'bg-e2o-500 text-white' : 'bg-white dark:bg-neutral-900 text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}>
              <CalendarCheck size={14} /> Post événement
            </button>
          </div>

          {/* ── Rétention natif — inclusion dans l'e-mail ─────────────────────── */}
          {nativeInfo?.has_native && (
            <label className={`flex items-start gap-2.5 rounded-lg border p-3 text-sm cursor-pointer transition-colors ${nativeInfo.is_expired ? 'border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20' : 'border-gray-200 dark:border-neutral-800'}`}>
              <input type="checkbox" checked={includeNativeLink} onChange={e => setIncludeNativeLink(e.target.checked)}
                className="mt-0.5 accent-e2o-500" />
              <span className="text-gray-700 dark:text-neutral-300">
                Inclure le lien de téléchargement du fichier natif dans l'e-mail
                {nativeInfo.is_expired ? (
                  <span className="block text-xs text-red-500 dark:text-red-400 mt-0.5">Fichier expiré depuis le {nativeInfo.expires_at?.slice(0, 10)} — le lien reste fonctionnel mais n'est plus signalé comme conservé.</span>
                ) : (
                  <span className="block text-xs text-gray-400 dark:text-neutral-500 mt-0.5">Conservé jusqu'au {nativeInfo.expires_at?.slice(0, 10)} (J-{nativeInfo.days_remaining}).</span>
                )}
              </span>
            </label>
          )}

          {/* ── Phase 1 : Sur site ───────────────────────────────────────────── */}
          {phase === 'onsite' && (
            <div className="space-y-4">

              {/* Message Jour J */}
              <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Message Jour J</p>
                  <div className="flex items-center gap-2">
                    {ficheUrl && (
                      <a href={ficheUrl} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 text-xs text-teal-600 dark:text-teal-400 hover:underline">
                        <ExternalLink size={12} /> Fiche
                      </a>
                    )}
                    <button onClick={() => navigator.clipboard.writeText(onsiteMessage).then(() => { setOnsiteCopied(true); setTimeout(() => setOnsiteCopied(false), 2000); })}
                      className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 text-gray-600 dark:text-neutral-300 transition-colors">
                      {onsiteCopied ? <CheckCircle size={12} className="text-green-500" /> : <Copy size={12} />}
                      {onsiteCopied ? 'Copié !' : 'Copier'}
                    </button>
                  </div>
                </div>
                <div dangerouslySetInnerHTML={{ __html: onsiteMessage }}
                  className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden" />
                {/* textarea value={onsiteMessage} onChange={e => setOnsiteMessage(e.target.value)} — réactiver pour permettre l'édition manuelle */}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Envoyer la fiche au contact</p>

                {/* Champs supplémentaires email */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">
                      Hashtag événement
                    </label>
                    <input type="text" value={hashtag} readOnly
                      className="w-full text-xs border border-gray-200 dark:border-neutral-700 rounded-md px-2.5 py-1.5 bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">
                      Lien page LinkedIn communauté
                    </label>
                    <input type="url" value={linkedinCommunityLink}
                      onChange={e => setLinkedinCommunityLink(e.target.value)}
                      placeholder="https://linkedin.com/company/..."
                      className="w-full text-xs border border-gray-200 dark:border-neutral-700 rounded-md px-2.5 py-1.5 bg-white dark:bg-neutral-900 text-gray-700 dark:text-neutral-200 placeholder-gray-300 dark:placeholder-neutral-600 focus:outline-none focus:ring-1 focus:ring-e2o-400" />
                  </div>
                </div>

                {!hasEmail && <p className="text-xs text-amber-600 dark:text-amber-400">Email du contact non renseigné.</p>}
                {!ficheUrl && <p className="text-xs text-amber-600 dark:text-amber-400">Fiche non disponible.</p>}
                <button onClick={() => handleSendEmail('onsite')} disabled={!hasEmail || !ficheUrl || emailStatus === 'sending'}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${btnClass}`}>
                  {btnContent}
                </button>
                {contact.mail && <p className="text-xs text-gray-400 dark:text-neutral-500 text-center">Destinataire : {contact.mail}</p>}
              </div>
            </div>
          )}

          {/* ── Phase 2 : Post événement ─────────────────────────────────────── */}
          {phase === 'postevent' && (
            <div className="space-y-4">

              {/* Message de livraison */}
              <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Message de livraison</p>
                  <button onClick={() => navigator.clipboard.writeText(message).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })}
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 text-gray-600 dark:text-neutral-300 transition-colors">
                    {copied ? <CheckCircle size={12} className="text-green-500" /> : <Copy size={12} />}
                    {copied ? 'Copié !' : 'Copier'}
                  </button>
                </div>
                <div dangerouslySetInnerHTML={{ __html: message }}
                  className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden" />
                {/* textarea value={message} onChange={e => setMessage(e.target.value)} — réactiver pour permettre l'édition manuelle */}
              </div>

              {/* Post LinkedIn — masqué pour animateur */}
              {!isAnimateur && (
                <div className="rounded-lg border border-[#0A66C2]/30 dark:border-[#0A66C2]/20 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                      <p className="text-sm font-semibold text-[#0A66C2]">Post LinkedIn — à partager par le speaker</p>
                    </div>
                    <button onClick={() => navigator.clipboard.writeText(linkedInPost).then(() => { setLinkedInCopied(true); setTimeout(() => setLinkedInCopied(false), 2000); })}
                      className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-[#0A66C2]/30 hover:bg-blue-50 dark:hover:bg-[#0A66C2]/10 text-[#0A66C2] transition-colors">
                      {linkedInCopied ? <CheckCircle size={12} className="text-green-500" /> : <Copy size={12} />}
                      {linkedInCopied ? 'Copié !' : 'Copier'}
                    </button>
                  </div>
                  <textarea value={linkedInPost} onChange={e => setLinkedInPost(e.target.value)} rows={10}
                    className="w-full text-sm leading-relaxed border border-[#0A66C2]/20 dark:border-[#0A66C2]/10 rounded-lg px-3 py-2.5 bg-blue-50/40 dark:bg-[#0A66C2]/5 text-gray-700 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-[#0A66C2]/40 resize-none" />
                  <p className="text-xs text-gray-400 dark:text-neutral-500">À transmettre au speaker pour publication sur son profil LinkedIn.</p>
                </div>
              )}

              {/* Envoi email */}
              <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Envoyer le lien vidéo au contact</p>
                {!hasEmail && <p className="text-xs text-amber-600 dark:text-amber-400">Email du contact non renseigné.</p>}
                {!hasVideo && <p className="text-xs text-amber-600 dark:text-amber-400">Aucune vidéo disponible pour l'envoi.</p>}
                <button onClick={() => handleSendEmail('postevent')} disabled={!hasEmail || !hasVideo || emailStatus === 'sending'}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${btnClass}`}>
                  {btnContent}
                </button>
                {contact.mail && <p className="text-xs text-gray-400 dark:text-neutral-500 text-center">Destinataire : {contact.mail}</p>}
              </div>

            </div>
          )}

          {/* ── Phase 3 : Diffusion complète (3 blocs I/II/III) ─────────────── */}
          {phase === 'complete' && (
            <div className="space-y-4">

              {/* Message complet */}
              <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Message de diffusion complet</p>
                  <button onClick={() => navigator.clipboard.writeText(fullMessage).then(() => { setFullCopied(true); setTimeout(() => setFullCopied(false), 2000); })}
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 text-gray-600 dark:text-neutral-300 transition-colors">
                    {fullCopied ? <CheckCircle size={12} className="text-green-500" /> : <Copy size={12} />}
                    {fullCopied ? 'Copié !' : 'Copier'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 dark:text-neutral-500">
                  I. Voir l'interview en ligne — II. Récupérer la vidéo native — III. Relai animateur
                  {!animateurContactForDelivery && ' (bloc III masqué : pas d\'animateur distinct pour ce créneau)'}
                </p>
                <div dangerouslySetInnerHTML={{ __html: fullMessage }}
                  className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden" />
              </div>

              {/* Envoi email */}
              <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200">Envoyer le lien vidéo au contact</p>
                {!hasEmail && <p className="text-xs text-amber-600 dark:text-amber-400">Email du contact non renseigné.</p>}
                {!hasVideo && <p className="text-xs text-amber-600 dark:text-amber-400">Aucune vidéo disponible pour l'envoi.</p>}
                <button onClick={() => handleSendEmail('complete')} disabled={!hasEmail || !hasVideo || emailStatus === 'sending'}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${btnClass}`}>
                  {btnContent}
                </button>
                {contact.mail && <p className="text-xs text-gray-400 dark:text-neutral-500 text-center">Destinataire : {contact.mail}</p>}
              </div>

            </div>
          )}

          {/* ── Historique des envois — commun aux trois phases ────────────── */}
          {sendHistory.length > 0 && (
            <div className="rounded-lg border border-gray-200 dark:border-neutral-800 p-4 space-y-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">Historique des envois</p>
              <ul className="space-y-1.5">
                {sendHistory.map(log => (
                  <li key={log.id_activity_log} className="flex items-center justify-between gap-3 text-xs text-gray-600 dark:text-neutral-400">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Mail size={11} className="text-e2o-400 shrink-0" />
                      <span className="truncate">{log.metadata?.email || contact?.mail}</span>
                      {log.metadata?.type && (
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${log.metadata.type === 'onsite' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300' : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300'}`}>
                          {log.metadata.type === 'onsite' ? 'Jour J' : 'Post-évt'}
                        </span>
                      )}
                    </span>
                    <span className="text-gray-400 dark:text-neutral-500 shrink-0">{formatRelativeDate(log.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Tab: Collectifs / Plans d'action (cycle_lang) ───────────────────────────

const TabCycles = ({ idVideo, apiUrlTranslation }) => {
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(null);
  const [removing, setRemoving] = useState(null);
  const searchTimeout = useRef(null);

  const fetchLinks = useCallback(() => {
    if (!idVideo) return;
    setLinksLoading(true);
    fetch(`${API_URL}?action=getVideosCycleLangList&filter=${encodeURIComponent(`WHERE id_video = ${idVideo}`)}`)
      .then(r => r.json())
      .then(data => setLinks(Array.isArray(data) ? data : []))
      .catch(() => setLinks([]))
      .finally(() => setLinksLoading(false));
  }, [idVideo]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const linkedIds = new Set(links.map(l => String(l.id_cycle_lang?.id_cycle_lang || l.id_cycle_lang)));

  // Recherche à la demande avec debounce
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(searchTimeout.current);
    if (val.trim().length < 2) { setResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const filter = encodeURIComponent(`nom_cycle_lang LIKE '%${val.trim()}%' AND `);
        const res = await fetch(
          `${apiUrlTranslation}?action=getCycleLang&filter=${filter}&get_children=0&get_parents=0`
        );
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
  };

  const handleAdd = async (cycle) => {
    const idCycleLang = cycle.id_cycle_lang;
    if (linkedIds.has(String(idCycleLang))) return;
    setAdding(idCycleLang);
    try {
      await fetch(`${API_URL}?action=createVideosCycleLang`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_video: idVideo, id_cycle_lang: idCycleLang }),
      });
      fetchLinks();
    } finally { setAdding(null); }
  };

  const handleRemove = async (link) => {
    const linkId = link.id_videos_cycle_lang;
    setRemoving(linkId);
    try {
      await fetch(`${API_URL}?action=deleteVideosCycleLang`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_videos_cycle_lang: linkId }),
      });
      setLinks(prev => prev.filter(l => l.id_videos_cycle_lang !== linkId));
    } finally { setRemoving(null); }
  };

  const getCycleName = (link) =>
    link.id_cycle_lang?.nom_cycle_lang || `#${link.id_cycle_lang}`;

  const getCycleLogo = (link) =>
    link.id_cycle_lang?.logo_better?.small || null;

  return (
    <div className="space-y-5">
      {/* Liens existants */}
      <div>
        <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200 mb-2">
          Collectifs / Plans d'action liés
          <span className="ml-2 text-xs font-normal text-gray-400 dark:text-neutral-500">({links.length})</span>
        </p>
        {linksLoading ? (
          <div className="flex items-center gap-2 py-3 text-xs text-gray-400 dark:text-neutral-500">
            <SpinnerIcon size={13} className="animate-spin" /> Chargement…
          </div>
        ) : links.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-neutral-500 italic">Aucun collectif lié pour l'instant.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-neutral-800 rounded-lg border border-gray-100 dark:border-neutral-800 overflow-hidden">
            {links.map(link => (
              <li key={link.id_videos_cycle_lang} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-neutral-800 group">
                {getCycleLogo(link)
                  ? <img src={getCycleLogo(link)} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                  : <div className="w-7 h-7 rounded bg-gray-100 dark:bg-neutral-700 shrink-0 flex items-center justify-center text-gray-300 dark:text-neutral-500 text-xs font-bold">C</div>
                }
                <span className="flex-1 text-sm text-gray-700 dark:text-neutral-300 truncate">{getCycleName(link)}</span>
                <button
                  onClick={() => handleRemove(link)}
                  disabled={removing === link.id_videos_cycle_lang}
                  className="opacity-0 group-hover:opacity-100 text-gray-300 dark:text-neutral-600 hover:text-red-500 transition-opacity shrink-0 disabled:opacity-50"
                >
                  {removing === link.id_videos_cycle_lang
                    ? <SpinnerIcon size={13} className="animate-spin" />
                    : <X size={14} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recherche à la demande */}
      <div>
        <p className="text-sm font-semibold text-gray-700 dark:text-neutral-200 mb-2">Rechercher un collectif</p>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" />
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder="Saisir au moins 2 caractères…"
            className="w-full pl-9 pr-8 py-2.5 border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-e2o-400 placeholder:text-gray-400 dark:placeholder:text-neutral-500"
          />
          {searching && (
            <SpinnerIcon size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-e2o-400 animate-spin" />
          )}
        </div>

        {results.length > 0 && (
          <ul className="mt-1.5 divide-y divide-gray-100 dark:divide-neutral-800 rounded-lg border border-gray-100 dark:border-neutral-800 overflow-hidden max-h-72 overflow-y-auto">
            {results.map(cycle => {
              const isLinked = linkedIds.has(String(cycle.id_cycle_lang));
              const isAdding = adding === cycle.id_cycle_lang;
              return (
                <li key={cycle.id_cycle_lang}
                  className={`flex items-center gap-3 px-4 py-2.5 ${isLinked ? 'bg-e2o-50 dark:bg-e2o-950/30' : 'hover:bg-gray-50 dark:hover:bg-neutral-800'}`}>
                  {cycle.logo_better?.small
                    ? <img src={cycle.logo_better.small} alt="" className="w-7 h-7 rounded object-cover shrink-0" />
                    : <div className="w-7 h-7 rounded bg-gray-100 dark:bg-neutral-700 shrink-0 flex items-center justify-center text-gray-300 dark:text-neutral-500 text-xs font-bold">C</div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 dark:text-neutral-300 truncate">{cycle.nom_cycle_lang}</p>
                    {cycle.cycle_lang_type && (
                      <p className="text-xs text-gray-400 dark:text-neutral-500 truncate">{cycle.cycle_lang_type}</p>
                    )}
                  </div>
                  {isLinked ? (
                    <span className="shrink-0 flex items-center gap-1 text-xs text-e2o-500 font-medium">
                      <CheckCircle size={13} /> Lié
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAdd(cycle)}
                      disabled={isAdding}
                      className="shrink-0 flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-md disabled:opacity-50 transition-colors"
                    >
                      {isAdding ? <SpinnerIcon size={11} className="animate-spin" /> : <Plus size={11} />}
                      {isAdding ? '…' : 'Lier'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {query.trim().length >= 2 && !searching && results.length === 0 && (
          <p className="mt-2 text-xs text-gray-400 dark:text-neutral-500 italic px-1">Aucun résultat pour « {query} »</p>
        )}
      </div>
    </div>
  );
};

// ─── Sidebar participants ─────────────────────────────────────────────────────

const ParticipantMiniCard = ({ c, role, onEdit }) => (
  <div className="flex items-center gap-2.5 py-2 border-b border-gray-100 dark:border-neutral-800 last:border-0">
    {c.photos?.tiny || c.photos?.small || c.photo ? (
      <img
        src={c.photos?.tiny || c.photos?.small || (DIR_IMG_UPLOADED_CONTACTS + c.photo)}
        alt={`${c.prenom} ${c.nom}`}
        className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-100 dark:border-neutral-700"
      />
    ) : (
      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center shrink-0">
        <User size={14} className="text-gray-300 dark:text-neutral-600" />
      </div>
    )}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{c.prenom} {c.nom}</p>
      {c.societe && (
        <p className="text-xs text-gray-400 dark:text-neutral-500 truncate flex items-center gap-1">
          <Building size={10} className="shrink-0" /> {c.societe}
        </p>
      )}
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      {c.sn_linkedin && (
        <a href={c.sn_linkedin} target="_blank" rel="noopener noreferrer" title="Profil LinkedIn"
          className="text-[#0A66C2] hover:opacity-70 transition-opacity">
          {SOCIAL_ICONS.linkedin}
        </a>
      )}
      {role === 'animateur' && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 font-medium">
          🎙️
        </span>
      )}
      {onEdit && (
        <button onClick={() => onEdit(c.id_contact, role)} title="Éditer"
          className="p-1 text-gray-400 hover:text-e2o-500 dark:hover:text-e2o-400 transition-colors rounded">
          <Pencil size={12} />
        </button>
      )}
    </div>
  </div>
);

const ParticipantsSidebar = ({ confEventId, apiUrlTranslation, onEdit, refreshKey = 0 }) => {
  const [participants, setParticipants] = useState(null);

  useEffect(() => {
    if (!confEventId) return;
    fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${confEventId}`)
      .then(r => r.json())
      .then(data => setParticipants(Array.isArray(data) ? data : []))
      .catch(() => setParticipants([]));
  }, [confEventId, apiUrlTranslation, refreshKey]);

  const animateurs = (participants || []).filter(p => String(p.statut) === '27').map(p => p.contact).filter(Boolean);
  const intervenants = (participants || []).filter(p => String(p.statut) !== '27').map(p => p.contact).filter(Boolean);
  const total = animateurs.length + intervenants.length;

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide">
          Participants
          {total > 0 && <span className="ml-1.5 text-e2o-500">{total}</span>}
        </p>
        <button
          onClick={onEdit}
          className="flex items-center gap-1 text-xs text-e2o-500 hover:text-e2o-600 hover:underline"
        >
          Gérer
        </button>
      </div>

      {participants === null && (
        <p className="text-xs text-gray-400 dark:text-neutral-500 flex items-center gap-1.5">
          <SpinnerIcon size={12} className="animate-pulse" /> Chargement…
        </p>
      )}

      {participants !== null && total === 0 && (
        <p className="text-xs text-gray-400 dark:text-neutral-500">Aucun participant</p>
      )}

      {intervenants.length > 0 && (
        <div>
          {animateurs.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-neutral-500 font-medium mb-1">Intervenants</p>
          )}
          {intervenants.map(c => <ParticipantMiniCard key={c.id_contact} c={c} role="speaker" onEdit={onEdit} />)}
        </div>
      )}

      {animateurs.length > 0 && (
        <div className={intervenants.length > 0 ? 'mt-2' : ''}>
          {intervenants.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-neutral-500 font-medium mb-1 mt-2">Animateur</p>
          )}
          {animateurs.map(c => <ParticipantMiniCard key={c.id_contact} c={c} role="animateur" onEdit={onEdit} />)}
        </div>
      )}
    </div>
  );
};

// ─── TabParams ───────────────────────────────────────────────────────────────

const TabParams = ({ idVideo, idConfEvent, idEvent, youtubeId, organisationId, onDeleted }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [nativeStatus, setNativeStatus] = useState(null);
  const [retention, setRetention] = useState(null);
  const [retentionInput, setRetentionInput] = useState('');
  const [savingRetention, setSavingRetention] = useState(false);

  useEffect(() => {
    if (!idVideo) return;
    const fd = new FormData();
    fd.append('action', 'get_native_status');
    fd.append('id_video', idVideo);
    fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
      .then(r => r.json()).then(setNativeStatus).catch(() => { });
  }, [idVideo]);

  useEffect(() => {
    if (!idEvent) return;
    const fd = new FormData();
    fd.append('action', 'get_event_retention');
    fd.append('id_event', idEvent);
    fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
      .then(r => r.json())
      .then(d => { setRetention(d); setRetentionInput(String(d.retention_days ?? '')); })
      .catch(() => { });
  }, [idEvent]);

  const handleSaveRetention = async () => {
    const days = parseInt(retentionInput, 10);
    if (!idEvent || !days || days <= 0) return;
    setSavingRetention(true);
    try {
      const fd = new FormData();
      fd.append('action', 'set_event_retention');
      fd.append('id_event', idEvent);
      fd.append('retention_days', days);
      const d = await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd }).then(r => r.json());
      if (d.success) setRetention({ id_event: idEvent, retention_days: days, is_default: false });
    } finally { setSavingRetention(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (youtubeId) {
        const fd = new FormData();
        fd.append('action', 'delete_youtube_video');
        fd.append('id_youtube', youtubeId);
        fd.append('organisation_id', organisationId ?? 1);
        await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd });
      }
      if (idVideo) {
        await fetch(`${API_URL}?action=deleteVideo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_video: idVideo }),
        });
      }
      if (idConfEvent) {
        await fetch(`${API_URL}?action=deleteConferencier`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_conf_event: idConfEvent }),
        });
        await fetch(`${API_URL}?action=deleteConfEvent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_conf_event: idConfEvent }),
        });
      }
      onDeleted?.();
    } catch (err) {
      console.error('deleteSlot error:', err);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 dark:border-neutral-800 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-neutral-800/60 border-b border-gray-200 dark:border-neutral-800">
          <span className="text-xs font-semibold text-gray-600 dark:text-neutral-300 uppercase tracking-wide">Rétention du fichier natif</span>
        </div>
        <div className="px-4 py-4 bg-white dark:bg-neutral-900 space-y-3">
          {nativeStatus?.has_native ? (
            <p className={`text-sm ${nativeStatus.is_expired ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-neutral-300'}`}>
              {nativeStatus.is_expired
                ? `Fichier natif expiré depuis le ${nativeStatus.expires_at?.slice(0, 10)}.`
                : `Uploadé le ${nativeStatus.uploaded_at?.slice(0, 10)}, conservé jusqu'au ${nativeStatus.expires_at?.slice(0, 10)} (J-${nativeStatus.days_remaining}).`}
            </p>
          ) : (
            <p className="text-sm text-gray-400 dark:text-neutral-500 italic">Aucun fichier natif conservé pour cette vidéo.</p>
          )}
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">
                Durée de rétention de l'événement (jours)
                {retention?.is_default && <span className="italic"> — valeur par défaut</span>}
              </label>
              <input
                type="number"
                min="1"
                value={retentionInput}
                onChange={e => setRetentionInput(e.target.value)}
                className="w-28 px-3 py-2 text-sm bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg text-gray-700 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-e2o-400"
              />
            </div>
            <button
              onClick={handleSaveRetention}
              disabled={savingRetention || !idEvent}
              className="px-3 py-2 text-sm font-medium text-e2o-600 dark:text-e2o-400 border border-e2o-200 dark:border-e2o-800 hover:bg-e2o-50 dark:hover:bg-e2o-950/30 rounded-lg disabled:opacity-50 transition-colors"
            >
              {savingRetention ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-red-200 dark:border-red-900/60 overflow-hidden">
        <div className="px-4 py-3 bg-red-50 dark:bg-red-950/30 flex items-center gap-2 border-b border-red-200 dark:border-red-900/60">
          <AlertTriangle size={14} className="text-red-500 shrink-0" />
          <span className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide">Zone de danger</span>
        </div>
        <div className="px-4 py-4 bg-white dark:bg-neutral-900 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-800 dark:text-neutral-200">Supprimer ce slot</p>
            <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">
              Supprime définitivement la vidéo, les intervenants, la publication YouTube et le créneau associé.
            </p>
          </div>
          <button
            onClick={() => setConfirmDelete(true)}
            className="shrink-0 flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
          >
            <Trash2 size={14} />
            Supprimer
          </button>
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl w-full max-w-sm border border-red-200 dark:border-red-900/60">
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-950/50 flex items-center justify-center shrink-0">
                  <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">Supprimer ce slot ?</h3>
                  <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">Cette action est irréversible.</p>
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-lg px-3 py-2.5">
                <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
                  <strong>Attention, cette opération est irréversible.</strong> Toutes les informations liées à ce slot seront définitivement perdues : vidéo, transcription, intervenants, publication YouTube et créneau.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg disabled:opacity-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
                >
                  {deleting ? <SpinnerIcon size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  {deleting ? 'Suppression…' : 'Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function VideoEditPage() {
  const { id_event, id_video } = useParams();
  const history = useHistory();
  const { search } = useLocation();

  const { apiUrlTranslation } = useApiUrlTranslation();
  const { currentEvent, getEventByIdEvent } = useContext(EventContext);

  useEffect(() => {
    if (id_event) getEventByIdEvent(id_event);
  }, [id_event]); // eslint-disable-line react-hooks/exhaustive-deps

  const [loading, setLoading] = useState(true);
  const [slot, setSlot] = useState(null); // { contact, video (the id_video object), presta, conf_event, conferencier }
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(search).get('tab') || 'metadata');
  const [youtubeOrgs, setYoutubeOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(1);
  const [participantsKey, setParticipantsKey] = useState(0);
  const [speakerExpandId, setSpeakerExpandId] = useState(null);
  const [speakerExpandRole, setSpeakerExpandRole] = useState(null);
  const [speakerExpandKey, setSpeakerExpandKey] = useState(0);

  useEffect(() => {
    const fd = new FormData();
    fd.append('action', 'get_youtube_orgs');
    fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
      .then(r => r.json())
      .then(d => {
        if (d.orgs?.length) {
          setYoutubeOrgs(d.orgs);
          const def = d.orgs.find(o => o.is_default) || d.orgs[0];
          if (def) setSelectedOrgId(def.organisation_id);
        }
      })
      .catch(() => { });
  }, []);

  const fetchSlot = useCallback(async () => {
    if (!id_video || !apiUrlTranslation) return;
    setLoading(true);
    try {
      // 1. Fetch video record to get confEventId
      let videoObj = null;
      let confEventId = null;

      const videoData = await fetch(
        `${apiUrlTranslation}?action=getVideosConferences&filter=${encodeURIComponent(`WHERE v.id_video = ${id_video}`)}`
      ).then(r => r.json());

      if (Array.isArray(videoData) && videoData.length > 0) {
        const vr = videoData[0];
        videoObj = vr.id_video;
        confEventId = vr.id_conf_event?.id_conf_event || vr.id_conf_event;
      }

      // 2. Fetch participant using confEventId (or fall back to id_video as conf_event)
      let participant = null;
      const ceId = confEventId || id_video;
      const participantData = await fetch(
        `${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_conf_event=${ceId}`
      ).then(r => r.json());

      if (Array.isArray(participantData) && participantData.length > 0) {
        participant = participantData[0];
        if (!confEventId) confEventId = participant.conf_event?.id_conf_event;
      }

      // 3. If we have participant but no video, try fetching video by conf_event
      if (participant && !videoObj && confEventId) {
        const res = await fetch(`${apiUrlTranslation}?action=getVideosConferences&filter=${encodeURIComponent(`WHERE vc.id_conf_event = ${confEventId}`)}`);
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) videoObj = data[0].id_video;
      }

      // Fetch presta
      let presta = null;
      if (participant?.contact?.id_contact) {
        const res = await fetch(`${apiUrlTranslation}?action=getPrestaList&params=WHERE id_contact=${participant.contact.id_contact}`);
        const data = await res.json();
        presta = Array.isArray(data) ? data[0] : null;
      }

      setSlot({
        contact: participant?.contact || null,
        video: videoObj, // the nested video object with id_video, id_youtube, embed_url, etc.
        presta,
        conf_event: participant?.conf_event || null,
        conferencier: participant?.id_conferencier ? { id_conferencier: participant.id_conferencier } : null,
        confEventId,  // resolved integer — source of truth for TabSpeakers and TabMetadata
      });
    } catch (e) {
      console.error('VideoEditPage fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [id_video, apiUrlTranslation]);

  useEffect(() => { fetchSlot(); }, [fetchSlot]);

  const handleVideoUpdated = (updated) => {
    setSlot(prev => prev ? { ...prev, video: { ...prev.video, ...updated } } : prev);
  };

  const handleUploaded = (id_conf_event, id_video_new, youtube_id) => {
    setSlot(prev => prev ? {
      ...prev,
      video: { ...(prev.video || {}), id_video: id_video_new, id_youtube: youtube_id, video_url: `https://www.youtube.com/watch?v=${youtube_id}` }
    } : prev);
    logActivity({
      action_type: 'video_uploaded',
      id_contact_receiver: slot?.contact?.id_contact ?? null,
      id_event: id_event ?? null,
      id_conf_event: id_conf_event ?? null,
      metadata: {
        id_video: id_video_new,
        youtube_id,
      },
    });
  };

  const handleAddYoutubeLink = async (idVid, ytId, idCE) => {
    try {
      if (idVid) {
        await fetch(`${API_URL}?action=updateVideo`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_video: idVid, id_youtube: ytId }),
        });
        setSlot(prev => prev ? { ...prev, video: { ...prev.video, id_youtube: ytId, video_url: `https://www.youtube.com/watch?v=${ytId}` } } : prev);
      } else if (idCE) {
        const res = await fetch(`${API_URL}?action=createVideo`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_youtube: ytId, id_conf_event: idCE }),
        });
        const data = await res.json();
        const newIdVideo = data?.id_video;
        if (newIdVideo) {
          await fetch(`${API_URL}?action=createVideosConferences`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id_video: newIdVideo, id_conf_event: idCE }),
          });
          setSlot(prev => prev ? { ...prev, video: { id_video: newIdVideo, id_youtube: ytId, video_url: `https://www.youtube.com/watch?v=${ytId}` } } : prev);
        }
      }
    } catch (e) { alert('Erreur : ' + e.message); }
  };

  const handleSaveDownloadLink = async (idVid, url, idCE) => {
    try {
      await fetch(`${API_URL}?action=updateVideoDownloadLink`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_video: idVid, download_url: url, id_conf_event: idCE }),
      });
      setSlot(prev => prev ? { ...prev, video: { ...prev.video, download_url: url } } : prev);
    } catch (e) { alert('Erreur : ' + e.message); }
  };

  const { contact, video, presta, conf_event, confEventId: resolvedConfEventId } = slot || {};
  const title = presta?.presta_nom || [contact?.prenom, contact?.nom].filter(Boolean).join(' ') || 'Éditeur vidéo';
  const youtubeId = video?.id_youtube || (video?.video_url ? extractYoutubeId(video.video_url) : null);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        {/* Header */}
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
          <Link
            to={`/events/${id_event}/registration-dashboard-captations-admin`}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-neutral-400 hover:text-gray-800 dark:hover:text-white transition-colors shrink-0"
          >
            <ArrowLeft size={15} />
            Retour
          </Link>
          <div className="h-4 w-px bg-gray-200 dark:bg-neutral-700" />
          <h1 className="text-sm font-semibold text-gray-800 dark:text-white truncate">{title}</h1>
          <LangueBadge langue={presta?.langue_enregistrement} />
          <div className="ml-auto flex items-center gap-2">
            {youtubeId && (
              <a href={`https://www.youtube.com/watch?v=${youtubeId}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 dark:border-red-900 rounded-md hover:bg-red-50 dark:hover:bg-red-950 transition-colors">
                <Youtube size={13} /> YouTube
              </a>
            )}
            <span className="text-xs text-gray-400 dark:text-neutral-500 bg-gray-100 dark:bg-neutral-800 rounded px-2 py-1">
              #{id_video}
            </span>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerIcon size={24} className="animate-spin text-gray-400 dark:text-neutral-500" />
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 py-6 lg:flex lg:gap-6">
            {/* Left panel */}
            <div className="lg:w-80 xl:w-96 shrink-0 space-y-4 mb-6 lg:mb-0">
              {/* Video player */}
              <div className="rounded-xl overflow-hidden shadow-sm bg-black" style={{ aspectRatio: '16/9' }}>
                <VideoPlayer video={video} />
              </div>

              {/* Status badges */}
              <div className="flex flex-wrap gap-2">
                {youtubeId ? (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-100 rounded-full">
                    <Youtube size={11} /> Publié YouTube
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 rounded-full">
                    <Film size={11} /> Sans vidéo
                  </span>
                )}
                {video?.embed_url && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-100 rounded-full">
                    <CheckCircle size={11} /> Embed OK
                  </span>
                )}
              </div>

              {/* Slot info */}
              {conf_event && (
                <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-1">
                  <p className="text-xs font-semibold text-gray-500 dark:text-neutral-400 uppercase tracking-wide mb-2">Créneau</p>
                  <p className="text-sm text-gray-700 dark:text-neutral-300">#{conf_event.id_conf_event}</p>
                  {conf_event.conf_event_date && (
                    <p className="text-sm text-gray-600 dark:text-neutral-400">{conf_event.conf_event_date}</p>
                  )}
                  {(conf_event.heure_debut || conf_event.heure_fin) && (
                    <p className="text-sm text-gray-600 dark:text-neutral-400">{conf_event.heure_debut} – {conf_event.heure_fin}</p>
                  )}
                </div>
              )}

              {/* Participants list */}
              {resolvedConfEventId && (
                <ParticipantsSidebar
                  confEventId={resolvedConfEventId}
                  apiUrlTranslation={apiUrlTranslation}
                  refreshKey={participantsKey}
                  onEdit={(idContact) => { setActiveTab('speaker'); if (idContact) setSpeakerExpandId(idContact); setSpeakerExpandKey(k => k + 1); }}
                />
              )}
            </div>

            {/* Right panel */}
            <div className="flex-1 min-w-0">
              <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
                {/* Tab bar */}
                <div className="border-b border-gray-200 dark:border-neutral-800 px-4 overflow-x-auto">
                  <div className="flex gap-0 min-w-max">
                    {TABS.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                          ? 'border-e2o-500 text-e2o-500'
                          : 'border-transparent text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200 hover:border-gray-300 dark:hover:border-neutral-600'
                          }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab content */}
                <div className="p-6">
                  {activeTab === 'metadata' && (
                    <TabMetadata video={video} contact={contact} presta={presta} onVideoUpdated={handleVideoUpdated} organisationId={currentEvent?.organisation_id ?? 1} confEventId={resolvedConfEventId} />
                  )}
                  {activeTab === 'upload' && (
                    <TabUpload confEvent={conf_event} contact={contact} idEvent={id_event} idEventLigne={currentEvent?.event_ligne?.id_event_ligne} idVideo={video?.id_video} onUploaded={handleUploaded} organisationId={selectedOrgId} youtubeOrgs={youtubeOrgs} onOrgChange={setSelectedOrgId} lang={presta?.langue_enregistrement || 'fr'} />
                  )}
                  {activeTab === 'links' && (
                    <TabLinks video={video} confEvent={conf_event} onAddYoutubeLink={handleAddYoutubeLink} onSaveDownloadLink={handleSaveDownloadLink} />
                  )}
                  {activeTab === 'posts' && (
                    <TabPosts
                      idVideo={video?.id_video}
                      video={video}
                      lang={presta?.langue_enregistrement || 'fr'}
                      confEventId={resolvedConfEventId}
                      apiUrlTranslation={apiUrlTranslation}
                      onPostsRegenerated={data => setSlot(prev => prev ? {
                        ...prev,
                        video: {
                          ...prev.video,
                          linkedin_post_animateur: data.linkedin_post_animateur || prev.video?.linkedin_post_animateur,
                          linkedin_post_organisateur: data.linkedin_post_organisateur || prev.video?.linkedin_post_organisateur,
                          linkedin_post_interviewe: data.linkedin_post_interviewe || prev.video?.linkedin_post_interviewe,
                        }
                      } : prev)}
                    />
                  )}
                  {activeTab === 'cycles' && (
                    <TabCycles idVideo={video?.id_video} apiUrlTranslation={apiUrlTranslation} />
                  )}
                  {activeTab === 'speaker' && (
                    <TabSpeakers key={speakerExpandKey} contact={contact} onContactSaved={c => setSlot(prev => prev ? { ...prev, contact: c } : prev)} confEventId={resolvedConfEventId} idEvent={id_event} apiUrlTranslation={apiUrlTranslation} onParticipantAdded={() => setParticipantsKey(k => k + 1)} defaultExpandId={speakerExpandId} defaultExpandRole={speakerExpandRole} />
                  )}
                  {activeTab === 'delivery' && (
                    <TabDelivery
                      confEventId={resolvedConfEventId}
                      video={video} presta={presta}
                      idEvent={id_event} currentEvent={currentEvent}
                      apiUrlTranslation={apiUrlTranslation}
                    />
                  )}
                  {activeTab === 'params' && (
                    <TabParams
                      idVideo={video?.id_video}
                      idConfEvent={resolvedConfEventId}
                      idEvent={id_event}
                      youtubeId={youtubeId}
                      organisationId={currentEvent?.organisation_id}
                      onDeleted={() => history.push(`/events/${id_event}/registration-dashboard-captations`)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
